import React, { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../supabaseClient'
import { enviarEmailBaixaVistoria, buildBaixaEmailPayload } from '../services/vistoriaBaixaEmail'
import jsPDF from 'jspdf'
import * as XLSX from 'xlsx'
import './Vistorias.css'

/** Normaliza para busca: minúsculas e sem acentos. */
function normalizeTextBuscaCliente(text) {
  if (text == null || text === '') return ''
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function getNomeClienteOrdem(v) {
  if (!v) return ''
  const c = v.cliente
  if (!c) return ''
  const row = Array.isArray(c) ? c[0] : c
  return row?.nome_completo || ''
}

/** Administrativo vinculado a esta empresa vê todas as ordens (tipo Vistoria); demais administrativos só veem da própria empresa + instalações autorizadas. */
const EMPRESA_ID_ADMINISTRATIVO_VER_TODAS_OS = 'f0c6de5a-eaf9-4530-a6a7-28140091897d'

function isLikelyOfflineError(error) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  const msg = String(error?.message || error?.details || error?.hint || '').toLowerCase()
  const name = String(error?.name || '').toLowerCase()
  return (
    name === 'typeerror' ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('err_internet_disconnected') ||
    msg.includes('err_network') ||
    msg.includes('the internet connection appears to be offline')
  )
}

function vistoriaMedicaoFechada(vistoria) {
  return !!(vistoria?.vistoria_completa?.medicao_fechada || vistoria?.medicao_fechada)
}

const Vistorias = ({ onBack, userRole, userEmpresa, userId }) => {
  // Estados para modal de detalhes
  const [showDetalhesModal, setShowDetalhesModal] = useState(false)
  const [vistoriaDetalhes, setVistoriaDetalhes] = useState(null)
  
  // Estados para modal de baixa de vistoria
  const [showBaixaModal, setShowBaixaModal] = useState(false)
  const [vistoriaParaBaixa, setVistoriaParaBaixa] = useState(null)
  const [tiposPadrao, setTiposPadrao] = useState([])
  const [isLoadingBaixa, setIsLoadingBaixa] = useState(false)
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle')
  const [autoSaveLastAt, setAutoSaveLastAt] = useState(null)
  const [autoSaveErrorMessage, setAutoSaveErrorMessage] = useState('')
  const [autoSaveErrorOffline, setAutoSaveErrorOffline] = useState(false)
  const autoSaveTimeoutRef = useRef(null)
  const skipAutoSaveRef = useRef(true)
  const isAutoSavingRef = useRef(false)
  const lastAutoSavedPayloadRef = useRef('')
  
  // Estado para modal de loading do PDF
  const [showPdfLoadingModal, setShowPdfLoadingModal] = useState(false)
  
  // Estado para modal de sucesso
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [successWarning, setSuccessWarning] = useState('')
  
  // Estado para modal de escolha (Localização ou Baixar Vistoria)
  const [showEscolhaModal, setShowEscolhaModal] = useState(false)
  const [vistoriaParaEscolha, setVistoriaParaEscolha] = useState(null)
  
  // Estado para modal de informações do cliente (endereço e kit)
  const [showInfoClienteModal, setShowInfoClienteModal] = useState(false)
  const [infoCliente, setInfoCliente] = useState(null)
  
  // Estado para formulário de baixa
  const [formBaixa, setFormBaixa] = useState({
    tipo_padrao_id: '',
    ligacao_padrao: '',
    local_inversor: '',
    posicao_poste_concessionaria: '',
    conexao_inversor: '',
    local_aterramento_sistema: '',
    metragem_ca_inversor: '',
    metragem_cc: '',
    metragem_ca_padrao_fases: '',
    percurso_aterramento: '',
    qtd_conduletes_inversor: '',
    qtd_eletroduto_inversor: '',
    qtd_eletroduto_padrao: '',
    qtd_modulos_telhado: '',
    caminho_img_padrao: '',
    caminho_img_ramal: '',
    caminho_img_inversor: '',
    caminho_img_fachada: '',
    caminho_img_mapa: '',
    caminho_img_telhado: '',
    caminho_img_quadro: '',
    caminho_img_estrutura: '',
    caminho_img_croqui: '',
    observacoes: '',
    km_rodado: '',
    tipo_veiculo: '',
    data_vistoria: new Date().toISOString().split('T')[0],
    status: 'Novo'
  })
  
  // Estados para upload de imagens
  const [uploadingImages, setUploadingImages] = useState({})
  const [imageFiles, setImageFiles] = useState({})
  // Estados para múltiplas imagens (telhado e estrutura)
  const [multipleImageFiles, setMultipleImageFiles] = useState({
    telhado: [],
    estrutura: []
  })
  const [showImageSourceModal, setShowImageSourceModal] = useState(false)
  const [currentImageType, setCurrentImageType] = useState(null)
  const [imageInputRefs, setImageInputRefs] = useState({})
  // Parâmetros de vistoria (valor do km / almoço) da empresa, usados no cálculo do deslocamento
  const [parametrosVistoriaEmpresa, setParametrosVistoriaEmpresa] = useState([])

  const getIdClienteFromVistoria = (vistoria) => {
    if (!vistoria) return null
    if (vistoria.id_cliente) return vistoria.id_cliente
    if (vistoria.cliente?.id) return vistoria.cliente.id
    return null
  }
  
  // Detectar se é dispositivo móvel (reativo ao redimensionamento, para tabela vs cards)
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(max-width: 768px)').matches || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  })
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)')
    const handle = () => setIsMobile(mql.matches)
    mql.addEventListener('change', handle)
    return () => mql.removeEventListener('change', handle)
  }, [])
  
  // Estados para listagem de vistorias
  const [vistorias, setVistorias] = useState([])
  const [isLoadingVistorias, setIsLoadingVistorias] = useState(true)
  // Status disponíveis (conforme constraint da tabela vistorias)
  // Padrão do filtro: vistoriador → Novo + Em andamento; demais perfis → Todos (sem filtrar por status)
  const OPCOES_STATUS_VISTORIA = [
    'Novo',
    'Em andamento',
    'Pendente',
    'Concluida',
    'Revisado',
    'Cancelada',
    'Aprovado',
    'Aprovado com Obra',
    'Cancelado',
    'Reprovado'
  ]

  const [filtros, setFiltros] = useState({
    cliente: '',
    status: userRole === 'vistoriador' ? ['Novo', 'Em andamento'] : [],
    dataInicio: '',
    dataFim: ''
  })
  /** Texto do campo "Buscar por cliente..."; o filtro aplicado é `filtros.cliente` (commit ao Enter / Aplicar / Atualizar). */
  const [buscaClienteInput, setBuscaClienteInput] = useState('')
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const statusDropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setShowStatusDropdown(false)
      }
    }
    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showStatusDropdown])
  

  // Carregar dados iniciais
  useEffect(() => {
    loadVistorias()
    loadTiposPadrao()
  }, [])

  // Carregar tipos de padrão
  const loadTiposPadrao = async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_padrao')
        .select('id, nome')
        .order('nome')
      
      if (error) throw error
      setTiposPadrao(data || [])
    } catch (error) {
      console.error('Erro ao carregar tipos de padrão:', error)
    }
  }
  
  // Abrir modal de escolha quando status for "Novo"
  const openEscolhaModal = (vistoria) => {
    setVistoriaParaEscolha(vistoria)
    setShowEscolhaModal(true)
  }
  
  // Fechar modal de escolha
  const closeEscolhaModal = () => {
    setShowEscolhaModal(false)
    setVistoriaParaEscolha(null)
  }
  
  // Abrir informações do cliente (endereço e kit)
  const handleAbrirInformacoesCliente = async () => {
    if (!vistoriaParaEscolha) return
    
    const idCliente = vistoriaParaEscolha.id_cliente || vistoriaParaEscolha.cliente?.id
    
    if (idCliente) {
      try {
        // Buscar dados completos do cliente incluindo endereço e kit solar
        const { data: clienteData, error } = await supabase
          .from('clientes_cadastro')
          .select(`
            nome_completo,
            cpf,
            telefone,
            email,
            logradouro,
            numero,
            complemento,
            bairro,
            cidade,
            estado,
            cep,
            link_localizacao,
            kit_solar_cliente (
              marca_inversor,
              potencia_inversor,
              quantidade_inversor,
              marca_modulo,
              potencia_modulo,
              quantidade_modulo
            )
          `)
          .eq('id', idCliente)
          .single()
        
        if (error) throw error
        
        setInfoCliente(clienteData)
        setShowInfoClienteModal(true)
      } catch (error) {
        console.error('Erro ao buscar informações do cliente:', error)
        alert('Erro ao buscar informações do cliente: ' + error.message)
      }
    }
    
    closeEscolhaModal()
  }
  
  // Fechar modal de informações do cliente
  const closeInfoClienteModal = () => {
    setShowInfoClienteModal(false)
    setInfoCliente(null)
  }

  // Abrir modal de informações do cliente (endereço e kit) a partir de uma vistoria (ex.: nos cards mobile)
  const openInfoClienteModal = async (vistoria) => {
    const idCliente = vistoria.id_cliente || vistoria.cliente?.id
    if (!idCliente) return
    try {
      const { data: clienteData, error } = await supabase
        .from('clientes_cadastro')
        .select(`
          nome_completo,
          cpf,
          telefone,
          email,
          logradouro,
          numero,
          complemento,
          bairro,
          cidade,
          estado,
          cep,
          link_localizacao,
          kit_solar_cliente (
            marca_inversor,
            potencia_inversor,
            quantidade_inversor,
            marca_modulo,
            potencia_modulo,
            quantidade_modulo
          )
        `)
        .eq('id', idCliente)
        .single()
      if (error) throw error
      setInfoCliente(clienteData)
      setShowInfoClienteModal(true)
    } catch (err) {
      console.error('Erro ao buscar informações do cliente:', err)
      alert('Erro ao buscar informações do cliente: ' + (err.message || err))
    }
  }
  
  // Carregar parâmetros de vistoria (valor km / almoço) da empresa
  const loadParametrosVistoriaEmpresa = async () => {
    const empresaId = userEmpresa?.id || userEmpresa
    if (!empresaId) {
      setParametrosVistoriaEmpresa([])
      return
    }
    try {
      const { data, error } = await supabase
        .from('parametros_vistorias')
        .select('*')
        .eq('id_empresa', empresaId)
      if (error) throw error
      setParametrosVistoriaEmpresa(data || [])
    } catch (err) {
      console.error('Erro ao carregar parâmetros de vistoria:', err)
      setParametrosVistoriaEmpresa([])
    }
  }

  // Calcular deslocamento com base nos parâmetros da empresa e no tipo de veículo
  const calcularDeslocamentoBaixa = () => {
    const kmNum = parseFloat(String(formBaixa.km_rodado).replace(',', '.')) || 0
    if (kmNum <= 0) return null
    // Abaixo de 100km é considerado moto; acima, usa o veículo selecionado.
    const veiculo = kmNum >= 100 ? formBaixa.tipo_veiculo : 'moto'
    if (!veiculo) return null
    const param = parametrosVistoriaEmpresa.find(p => p.tipo_veiculo === veiculo)
    if (!param) return null
    const valorKm = Number(param.valor_km) || 0
    const valorAlmoco = Number(param.valor_almoco) || 0
    const kmAlmoco = Number(param.km_almoco) || 0
    const base = valorKm * kmNum
    // Soma o almoço quando ultrapassa o limite configurado (km_almoco)
    const aplicaAlmoco = kmAlmoco > 0 && kmNum >= kmAlmoco
    const total = base + (aplicaAlmoco ? valorAlmoco : 0)
    return { valorKm, valorAlmoco, kmAlmoco, base, aplicaAlmoco, total }
  }

  // Abrir modal de baixa
  const openBaixaModal = async (vistoria) => {
    skipAutoSaveRef.current = true
    setAutoSaveStatus('idle')
    setAutoSaveLastAt(null)
    setAutoSaveErrorMessage('')
    setAutoSaveErrorOffline(false)
    lastAutoSavedPayloadRef.current = ''
    setVistoriaParaBaixa(vistoria)
    loadParametrosVistoriaEmpresa()
    
    // Verificar se já existe vistoria cadastrada
    // Preferir sempre o dado mais recente do banco para evitar cache desatualizado na tela
    let vistoriaExistente = null
    const idCliente = vistoria.id_cliente || vistoria.cliente?.id
    
    // Buscar a vistoria mais recente do cliente no banco
    if (idCliente) {
      const { data: vistoriaData, error: vistoriaError } = await supabase
        .from('vistorias')
        .select('*')
        .eq('id_cliente', idCliente)
        .order('data_criacao', { ascending: false })
        .limit(1)
        .maybeSingle()
      
      if (!vistoriaError && vistoriaData) {
        vistoriaExistente = vistoriaData
      } else if (vistoria?.vistoria_completa) {
        // Fallback: usar cache local caso a busca falhe
        vistoriaExistente = vistoria.vistoria_completa
      }
    } else if (vistoria?.vistoria_completa) {
      vistoriaExistente = vistoria.vistoria_completa
    }
    
    // Preencher formulário com dados existentes ou valores padrão
    if (vistoriaExistente) {
      // Parsear JSON arrays para telhado e estrutura
      let caminhoImgTelhado = ''
      let caminhoImgEstrutura = ''
      let telhadoImages = []
      let estruturaImages = []
      
      try {
        if (vistoriaExistente.caminho_img_telhado) {
          const telhadoUrls = typeof vistoriaExistente.caminho_img_telhado === 'string' 
            ? JSON.parse(vistoriaExistente.caminho_img_telhado) 
            : vistoriaExistente.caminho_img_telhado
          if (Array.isArray(telhadoUrls)) {
            caminhoImgTelhado = vistoriaExistente.caminho_img_telhado
            telhadoImages = telhadoUrls.map((url, idx) => ({ url, name: `Imagem ${idx + 1}` }))
          } else {
            caminhoImgTelhado = vistoriaExistente.caminho_img_telhado
            telhadoImages = [{ url: vistoriaExistente.caminho_img_telhado, name: 'Imagem' }]
          }
        }
        if (vistoriaExistente.caminho_img_estrutura) {
          const estruturaUrls = typeof vistoriaExistente.caminho_img_estrutura === 'string' 
            ? JSON.parse(vistoriaExistente.caminho_img_estrutura) 
            : vistoriaExistente.caminho_img_estrutura
          if (Array.isArray(estruturaUrls)) {
            caminhoImgEstrutura = vistoriaExistente.caminho_img_estrutura
            estruturaImages = estruturaUrls.map((url, idx) => ({ url, name: `Imagem ${idx + 1}` }))
          } else {
            caminhoImgEstrutura = vistoriaExistente.caminho_img_estrutura
            estruturaImages = [{ url: vistoriaExistente.caminho_img_estrutura, name: 'Imagem' }]
          }
        }
      } catch (e) {
        console.warn('Erro ao parsear URLs de imagens:', e)
        // Se falhar o parse, tentar usar como string única
        if (vistoriaExistente.caminho_img_telhado) {
          caminhoImgTelhado = vistoriaExistente.caminho_img_telhado
          telhadoImages = [{ url: vistoriaExistente.caminho_img_telhado, name: 'Imagem' }]
        }
        if (vistoriaExistente.caminho_img_estrutura) {
          caminhoImgEstrutura = vistoriaExistente.caminho_img_estrutura
          estruturaImages = [{ url: vistoriaExistente.caminho_img_estrutura, name: 'Imagem' }]
        }
      }
      
      setFormBaixa({
        tipo_padrao_id: vistoriaExistente.tipo_padrao_id || '',
        ligacao_padrao: vistoriaExistente.ligacao_padrao || '',
        local_inversor: vistoriaExistente.local_inversor || '',
        posicao_poste_concessionaria: vistoriaExistente.posicao_poste_concessionaria || '',
        conexao_inversor: vistoriaExistente.conexao_inversor || '',
        local_aterramento_sistema: vistoriaExistente.local_aterramento_sistema || '',
        metragem_ca_inversor: vistoriaExistente.metragem_ca_inversor ? String(vistoriaExistente.metragem_ca_inversor) : '',
        metragem_cc: vistoriaExistente.metragem_cc ? String(vistoriaExistente.metragem_cc) : '',
        metragem_ca_padrao_fases: vistoriaExistente.metragem_ca_padrao_fases ? String(vistoriaExistente.metragem_ca_padrao_fases) : '',
        percurso_aterramento: vistoriaExistente.percurso_aterramento ? String(vistoriaExistente.percurso_aterramento) : '',
        qtd_conduletes_inversor: vistoriaExistente.qtd_conduletes_inversor ? String(vistoriaExistente.qtd_conduletes_inversor) : '',
        qtd_eletroduto_inversor: vistoriaExistente.qtd_eletroduto_inversor ? String(vistoriaExistente.qtd_eletroduto_inversor) : '',
        qtd_eletroduto_padrao: vistoriaExistente.qtd_eletroduto_padrao ? String(vistoriaExistente.qtd_eletroduto_padrao) : '',
        qtd_modulos_telhado: vistoriaExistente.qtd_modulos_telhado ? String(vistoriaExistente.qtd_modulos_telhado) : '',
        caminho_img_padrao: vistoriaExistente.caminho_img_padrao || '',
        caminho_img_ramal: vistoriaExistente.caminho_img_ramal || '',
        caminho_img_inversor: vistoriaExistente.caminho_img_inversor || '',
        caminho_img_fachada: vistoriaExistente.caminho_img_fachada || '',
        caminho_img_mapa: vistoriaExistente.caminho_img_mapa || '',
        caminho_img_telhado: caminhoImgTelhado,
        caminho_img_quadro: vistoriaExistente.caminho_img_quadro || '',
        caminho_img_estrutura: caminhoImgEstrutura,
        caminho_img_croqui: vistoriaExistente.caminho_img_croqui || '',
        observacoes: vistoriaExistente.observacoes || '',
        km_rodado: (vistoriaExistente.km_rodado !== null && vistoriaExistente.km_rodado !== undefined) ? String(vistoriaExistente.km_rodado) : '',
        tipo_veiculo: vistoriaExistente.tipo_veiculo || '',
        data_vistoria: vistoriaExistente.data_vistoria || new Date().toISOString().split('T')[0],
        status: vistoriaExistente.status || 'Novo'
      })
      
      // Preencher imagens únicas
      const imageFilesObj = {}
      if (vistoriaExistente.caminho_img_padrao) {
        imageFilesObj.padrao = { url: vistoriaExistente.caminho_img_padrao, name: 'Padrão' }
      }
      if (vistoriaExistente.caminho_img_ramal) {
        imageFilesObj.ramal = { url: vistoriaExistente.caminho_img_ramal, name: 'Ramal de Entrada' }
      }
      if (vistoriaExistente.caminho_img_inversor) {
        imageFilesObj.inversor = { url: vistoriaExistente.caminho_img_inversor, name: 'Inversor' }
      }
      if (vistoriaExistente.caminho_img_fachada) {
        imageFilesObj.fachada = { url: vistoriaExistente.caminho_img_fachada, name: 'Fachada' }
      }
      if (vistoriaExistente.caminho_img_mapa) {
        imageFilesObj.mapa = { url: vistoriaExistente.caminho_img_mapa, name: 'Mapa' }
      }
      if (vistoriaExistente.caminho_img_quadro) {
        imageFilesObj.quadro = { url: vistoriaExistente.caminho_img_quadro, name: 'Quadro' }
      }
      setImageFiles(imageFilesObj)
      
      // Preencher múltiplas imagens
      setMultipleImageFiles({
        telhado: telhadoImages,
        estrutura: estruturaImages
      })
      
      // Armazenar ID da vistoria existente para UPDATE
      setVistoriaParaBaixa({
        ...vistoria,
        vistoria_id: vistoriaExistente.id,
        medicao_fechada: !!vistoriaExistente.medicao_fechada,
        vistoria_completa: {
          ...(vistoria.vistoria_completa || {}),
          ...vistoriaExistente
        }
      })
    } else {
      // Formulário vazio para nova vistoria
      setFormBaixa({
      tipo_padrao_id: '',
      ligacao_padrao: '',
      local_inversor: '',
      posicao_poste_concessionaria: '',
      conexao_inversor: '',
      local_aterramento_sistema: '',
      metragem_ca_inversor: '',
      metragem_cc: '',
      metragem_ca_padrao_fases: '',
      percurso_aterramento: '',
      qtd_conduletes_inversor: '',
      qtd_eletroduto_inversor: '',
      qtd_eletroduto_padrao: '',
      qtd_modulos_telhado: '',
      caminho_img_padrao: '',
      caminho_img_ramal: '',
      caminho_img_inversor: '',
      caminho_img_fachada: '',
      caminho_img_mapa: '',
      caminho_img_telhado: '',
      caminho_img_quadro: '',
        caminho_img_estrutura: '',
        caminho_img_croqui: '',
        observacoes: '',
      km_rodado: '',
      tipo_veiculo: '',
      data_vistoria: new Date().toISOString().split('T')[0],
        status: 'Novo'
      })
      setImageFiles({})
      setMultipleImageFiles({ telhado: [], estrutura: [] })
    }
    
    setUploadingImages({})
    setShowBaixaModal(true)
    setTimeout(() => {
      skipAutoSaveRef.current = false
    }, 0)
  }
  
  // Fechar modal de baixa
  const closeBaixaModal = async () => {
    // Se houver auto-save pendente, tentar persistir antes de fechar
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
      autoSaveTimeoutRef.current = null
    }
    if (showBaixaModal && vistoriaParaBaixa) {
      await persistBaixaDraft({ silent: true })
    }

    skipAutoSaveRef.current = true
    setShowBaixaModal(false)
    setVistoriaParaBaixa(null)
    setImageFiles({})
    setMultipleImageFiles({ telhado: [], estrutura: [] })
    setUploadingImages({})
    loadVistorias()
  }

  const buildBaixaPayload = () => {
    const idCliente = getIdClienteFromVistoria(vistoriaParaBaixa)
    if (!idCliente) return null

    const empresaId =
      vistoriaParaBaixa?.id_empresa ||
      userEmpresa?.id ||
      (typeof userEmpresa === 'string' ? userEmpresa : null)

    return {
      id_cliente: idCliente,
      id_empresa: empresaId || null,
      tipo_padrao_id: formBaixa.tipo_padrao_id || null,
      ligacao_padrao: formBaixa.ligacao_padrao || null,
      local_inversor: formBaixa.local_inversor || null,
      posicao_poste_concessionaria: formBaixa.posicao_poste_concessionaria || null,
      conexao_inversor: formBaixa.conexao_inversor || null,
      local_aterramento_sistema: formBaixa.local_aterramento_sistema || null,
      metragem_ca_inversor: formBaixa.metragem_ca_inversor ? parseFloat(formBaixa.metragem_ca_inversor) : null,
      metragem_cc: formBaixa.metragem_cc ? parseFloat(formBaixa.metragem_cc) : null,
      metragem_ca_padrao_fases: formBaixa.metragem_ca_padrao_fases ? parseFloat(formBaixa.metragem_ca_padrao_fases) : null,
      percurso_aterramento: formBaixa.percurso_aterramento ? parseFloat(formBaixa.percurso_aterramento) : null,
      qtd_conduletes_inversor: formBaixa.qtd_conduletes_inversor ? parseInt(formBaixa.qtd_conduletes_inversor) : null,
      qtd_eletroduto_inversor: formBaixa.qtd_eletroduto_inversor ? parseInt(formBaixa.qtd_eletroduto_inversor) : null,
      qtd_eletroduto_padrao: formBaixa.qtd_eletroduto_padrao ? parseInt(formBaixa.qtd_eletroduto_padrao) : null,
      qtd_modulos_telhado: formBaixa.qtd_modulos_telhado ? parseInt(formBaixa.qtd_modulos_telhado) : null,
      caminho_img_padrao: formBaixa.caminho_img_padrao || null,
      caminho_img_ramal: formBaixa.caminho_img_ramal || imageFiles.ramal?.url || null,
      caminho_img_inversor: formBaixa.caminho_img_inversor || null,
      caminho_img_fachada: formBaixa.caminho_img_fachada || imageFiles.fachada?.url || null,
      caminho_img_mapa: formBaixa.caminho_img_mapa || imageFiles.mapa?.url || null,
      caminho_img_telhado: formBaixa.caminho_img_telhado || null,
      caminho_img_quadro: formBaixa.caminho_img_quadro || null,
      caminho_img_estrutura: formBaixa.caminho_img_estrutura || null,
      caminho_img_croqui: formBaixa.caminho_img_croqui || null,
      observacoes: formBaixa.observacoes || null,
      km_rodado: formBaixa.km_rodado ? parseFloat(String(formBaixa.km_rodado).replace(',', '.')) : null,
      tipo_veiculo: formBaixa.tipo_veiculo || null,
      data_vistoria: formBaixa.data_vistoria || null,
      status: formBaixa.status || 'Novo'
    }
  }

  const persistBaixaDraft = async ({ silent = true } = {}) => {
    if (!showBaixaModal || !vistoriaParaBaixa || skipAutoSaveRef.current || isAutoSavingRef.current) return null
    if (vistoriaMedicaoFechada(vistoriaParaBaixa)) return null
    const payload = buildBaixaPayload()
    if (!payload) return null

    const vistoriaId = vistoriaParaBaixa?.vistoria_id
    // A tabela vistorias exige tipo_padrao_id, ligacao_padrao e id_empresa (NOT NULL).
    // Sem isso o INSERT falha e o overlay mostrava "sem conexão" mesmo online.
    if (!vistoriaId && (!payload.tipo_padrao_id || !payload.ligacao_padrao || !payload.id_empresa)) {
      return null
    }
    if (vistoriaId) {
      if (!payload.tipo_padrao_id) delete payload.tipo_padrao_id
      if (!payload.ligacao_padrao) delete payload.ligacao_padrao
      if (!payload.id_empresa) delete payload.id_empresa
    }

    const payloadKey = JSON.stringify(payload)
    if (payloadKey === lastAutoSavedPayloadRef.current) return null

    try {
      isAutoSavingRef.current = true
      setAutoSaveStatus('saving')
      setAutoSaveErrorMessage('')
      setAutoSaveErrorOffline(false)

      let savedId = vistoriaId
      if (vistoriaId) {
        const { error } = await supabase
          .from('vistorias')
          .update(payload)
          .eq('id', vistoriaId)
        if (error) throw error
      } else {
        const { data, error } = await supabase
          .from('vistorias')
          .insert([payload])
          .select('id')
          .maybeSingle()
        if (error) throw error
        savedId = data?.id
        if (savedId) {
          setVistoriaParaBaixa(prev => (prev ? { ...prev, vistoria_id: savedId } : prev))
        }
      }

      lastAutoSavedPayloadRef.current = payloadKey
      setAutoSaveStatus('saved')
      setAutoSaveLastAt(new Date())
      return { id: savedId }
    } catch (error) {
      console.error('Erro no auto-save da baixa de vistoria:', error)
      const offline = isLikelyOfflineError(error)
      setAutoSaveErrorOffline(offline)
      setAutoSaveErrorMessage(error?.message || error?.details || 'Não foi possível salvar as informações.')
      setAutoSaveStatus('error')
      if (!silent) {
        alert('Erro ao salvar rascunho da baixa: ' + (error?.message || error))
      }
      return null
    } finally {
      isAutoSavingRef.current = false
    }
  }

  useEffect(() => {
    if (!showBaixaModal || !vistoriaParaBaixa || skipAutoSaveRef.current) return
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current)
    autoSaveTimeoutRef.current = setTimeout(() => {
      persistBaixaDraft({ silent: true })
    }, 700)

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
        autoSaveTimeoutRef.current = null
      }
    }
  }, [formBaixa, showBaixaModal, vistoriaParaBaixa])
  
  // Função para abrir menu de seleção de fonte de imagem (mobile)
  const handleImageSourceSelect = (imageType) => {
    if (isMobile) {
      setCurrentImageType(imageType)
      setShowImageSourceModal(true)
    } else {
      // Em desktop, abrir seletor de arquivo diretamente
      const input = document.getElementById(`img_${imageType}`)
      if (input) input.click()
    }
  }
  
  // Função para abrir menu de seleção de fonte de múltiplas imagens (mobile)
  const handleMultipleImageSourceSelect = (imageType) => {
    if (isMobile) {
      setCurrentImageType(imageType)
      setShowImageSourceModal(true)
    } else {
      // Em desktop, abrir seletor de arquivo diretamente
      const input = document.getElementById(`img_multiple_${imageType}`)
      if (input) input.click()
    }
  }
  
  // Função para selecionar fonte da imagem
  const selectImageSource = (useCamera) => {
    setShowImageSourceModal(false)
    const imageType = currentImageType
    const input = document.getElementById(`img_${imageType}`)
    if (input) {
      if (useCamera) {
        input.setAttribute('capture', 'environment')
      } else {
        input.removeAttribute('capture')
      }
      input.click()
    }
  }
  
  // Função para fazer upload de imagem única
  const handleImageUpload = async (imageType, file) => {
    if (!file) return
    
    // Validar se é uma imagem
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem')
      return
    }
    
    // Validar tamanho (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 10MB')
      return
    }

    try {
      setUploadingImages(prev => ({ ...prev, [imageType]: true }))
      
      // Obter id_cliente
      let idCliente = null
      if (vistoriaParaBaixa.id_cliente) {
        idCliente = vistoriaParaBaixa.id_cliente
      } else if (vistoriaParaBaixa.cliente?.id) {
        idCliente = vistoriaParaBaixa.cliente.id
      } else {
        alert('Erro: Cliente não identificado')
      return
      }
      
      // Gerar nome único para o arquivo
      const fileExt = file.name.split('.').pop()
      const timestamp = Date.now()
      const fileName = `${idCliente}/${imageType}_${timestamp}.${fileExt}`
      
      // Upload do arquivo para o Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('vistorias-fotos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })
      
      if (uploadError) {
        if (uploadError.message.includes('Bucket not found')) {
          throw new Error('❌ Bucket não encontrado! Você precisa criar o bucket "vistorias-fotos" no Supabase Storage primeiro.')
        }
        throw uploadError
      }
      
      // Obter URL pública do arquivo
      const { data: { publicUrl } } = supabase.storage
        .from('vistorias-fotos')
        .getPublicUrl(fileName)
      
      // Atualizar o campo correspondente no formulário
      setFormBaixa(prev => ({
        ...prev,
        [`caminho_img_${imageType}`]: publicUrl
      }))
      
      // Salvar referência do arquivo para preview
      setImageFiles(prev => ({
        ...prev,
        [imageType]: {
          file: file,
          url: publicUrl,
          name: file.name
        }
      }))
      
    } catch (error) {
      console.error(`Erro ao fazer upload da imagem ${imageType}:`, error)
      alert(`Erro ao fazer upload: ${error.message}`)
    } finally {
      setUploadingImages(prev => ({ ...prev, [imageType]: false }))
    }
  }
  
  // Função para fazer upload de múltiplas imagens
  const handleMultipleImageUpload = async (imageType, files) => {
    if (!files || files.length === 0) return
    
    // Validar todas as imagens
    for (let file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        alert(`A imagem ${file.name} deve ter no máximo 10MB`)
        return
      }
    }

    try {
      setUploadingImages(prev => ({ ...prev, [imageType]: true }))
      
      // Obter id_cliente
      let idCliente = null
      if (vistoriaParaBaixa.id_cliente) {
        idCliente = vistoriaParaBaixa.id_cliente
      } else if (vistoriaParaBaixa.cliente?.id) {
        idCliente = vistoriaParaBaixa.cliente.id
      } else {
        alert('Erro: Cliente não identificado')
        return
      }
      
      const uploadedUrls = []
      const uploadedFiles = []
      
      // Fazer upload de cada arquivo
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const fileExt = file.name.split('.').pop()
        const timestamp = Date.now() + i // Adicionar índice para garantir unicidade
        const fileName = `${idCliente}/${imageType}_${timestamp}.${fileExt}`
        
        // Upload do arquivo
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('vistorias-fotos')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          })
        
        if (uploadError) {
          if (uploadError.message.includes('Bucket not found')) {
            throw new Error('❌ Bucket não encontrado! Você precisa criar o bucket "vistorias-fotos" no Supabase Storage primeiro.')
          }
          throw uploadError
        }
        
        // Obter URL pública
        const { data: { publicUrl } } = supabase.storage
          .from('vistorias-fotos')
          .getPublicUrl(fileName)
        
        uploadedUrls.push(publicUrl)
        uploadedFiles.push({
          file: file,
          url: publicUrl,
          name: file.name
        })
      }
      
      // Salvar URLs como JSON array
      const urlsJson = JSON.stringify(uploadedUrls)
      
      // Atualizar o campo no formulário
      setFormBaixa(prev => ({
        ...prev,
        [`caminho_img_${imageType}`]: urlsJson
      }))
      
      // Atualizar múltiplas imagens
      setMultipleImageFiles(prev => ({
        ...prev,
        [imageType]: [...(prev[imageType] || []), ...uploadedFiles]
      }))
      
    } catch (error) {
      console.error(`Erro ao fazer upload das imagens ${imageType}:`, error)
      alert(`Erro ao fazer upload: ${error.message}`)
    } finally {
      setUploadingImages(prev => ({ ...prev, [imageType]: false }))
    }
  }
  
  // Função para selecionar fonte de múltiplas imagens
  const selectMultipleImageSource = (useCamera) => {
    setShowImageSourceModal(false)
    const imageType = currentImageType
    const input = document.getElementById(`img_multiple_${imageType}`)
    if (input) {
      if (useCamera) {
        input.setAttribute('capture', 'environment')
      } else {
        input.removeAttribute('capture')
      }
      input.click()
    }
  }
  
  // Função para remover imagem múltipla
  const handleRemoveMultipleImage = (imageType, index) => {
    const currentImages = multipleImageFiles[imageType] || []
    const newImages = currentImages.filter((_, i) => i !== index)
    setMultipleImageFiles(prev => ({
      ...prev,
      [imageType]: newImages
    }))
    
    // Atualizar campo no formulário
    const urls = newImages.map(img => img.url)
    setFormBaixa(prev => ({
      ...prev,
      [`caminho_img_${imageType}`]: urls.length > 0 ? JSON.stringify(urls) : ''
    }))
  }
  
  // Função para remover imagem
  const handleRemoveImage = (imageType) => {
    setFormBaixa(prev => ({
      ...prev,
      [`caminho_img_${imageType}`]: ''
    }))
    setImageFiles(prev => {
      const newFiles = { ...prev }
      delete newFiles[imageType]
      return newFiles
    })
  }
  
  // Salvar baixa de vistoria
  const handleSaveBaixa = async () => {
    try {
      setIsLoadingBaixa(true)

      if (vistoriaMedicaoFechada(vistoriaParaBaixa)) {
        alert('Medição fechada em Serviços. Não é possível alterar esta vistoria.')
        setIsLoadingBaixa(false)
        return
      }
      const vistoriaIdLock = vistoriaParaBaixa?.vistoria_id
      if (vistoriaIdLock) {
        const { data: lockRow } = await supabase
          .from('vistorias')
          .select('medicao_fechada')
          .eq('id', vistoriaIdLock)
          .maybeSingle()
        if (lockRow?.medicao_fechada) {
          alert('Medição fechada em Serviços. Não é possível alterar esta vistoria.')
          setIsLoadingBaixa(false)
          return
        }
      }
      
      // Validações obrigatórias
      if (!formBaixa.tipo_padrao_id) {
        alert('Por favor, selecione o tipo de padrão')
        setIsLoadingBaixa(false)
        return
      }
      if (!formBaixa.ligacao_padrao) {
        alert('Por favor, selecione o tipo de ligação')
        setIsLoadingBaixa(false)
        return
      }
      if (!formBaixa.status) {
        alert('Por favor, selecione o status da vistoria')
        setIsLoadingBaixa(false)
        return
      }
      // Se km rodado >= 100, é obrigatório selecionar o tipo de veículo
      const kmRodadoNum = parseFloat(String(formBaixa.km_rodado).replace(',', '.')) || 0
      if (kmRodadoNum >= 100 && !formBaixa.tipo_veiculo) {
        alert('Para Km Rodado igual ou maior que 100, selecione o tipo de veículo (Carro ou Moto)')
        setIsLoadingBaixa(false)
        return
      }
      // Validação de campos obrigatórios: Foto da Fachada e Link do Mapa
      const temFotoFachada = formBaixa.caminho_img_fachada || imageFiles.fachada?.url
      if (!temFotoFachada) {
        alert('Por favor, adicione a Foto da Fachada do Local')
        setIsLoadingBaixa(false)
        return
      }
      const temMapa = formBaixa.caminho_img_mapa || imageFiles.mapa?.url
      if (!temMapa) {
        alert('Por favor, adicione o Link do Mapa do Local da Instalação')
        setIsLoadingBaixa(false)
        return
      }
      
      // Obter id_cliente - pode estar em diferentes lugares dependendo da estrutura
      const idCliente = getIdClienteFromVistoria(vistoriaParaBaixa)
      if (!idCliente) {
        alert('Erro: Cliente não identificado')
        return
      }

      const dataToSave = buildBaixaPayload()
      if (!dataToSave) {
        alert('Erro: não foi possível montar os dados da baixa')
        return
      }
      
      // Verificar se já existe vistoria (para UPDATE) ou é nova (para INSERT)
      const vistoriaId = vistoriaParaBaixa?.vistoria_id
      
      let data, error
      if (vistoriaId) {
        // UPDATE - vistoria já existe
        const result = await supabase
          .from('vistorias')
          .update(dataToSave)
          .eq('id', vistoriaId)
          .select()
          .single()
        data = result.data
        error = result.error
      } else {
        // INSERT - nova vistoria
        const result = await supabase
          .from('vistorias')
          .insert([dataToSave])
          .select()
          .single()
        data = result.data
        error = result.error
      }

        if (error) throw error
      
      // Atualizar status da ordem de serviço para "Finalizado"
      const ordemServicoId = vistoriaParaBaixa.id
      if (ordemServicoId) {
        const { error: ordemError } = await supabase
          .from('ordens_servicos')
          .update({ status: 'Finalizado' })
          .eq('id', ordemServicoId)
        
        if (ordemError) {
          console.error('Erro ao atualizar status da ordem de serviço:', ordemError)
          // Não bloquear o fluxo se falhar, apenas logar o erro
        }
      }

      // E-mail de confirmação (Resend): usuário logado + cópia para demais responsáveis da OS
      let avisoEmail = ''
      if (userId) {
        const emailPayload = buildBaixaEmailPayload({
          userId,
          vistoriaParaBaixa,
          formBaixa,
          tiposPadrao,
          vistoriaId: data?.id || vistoriaId,
          atualizacao: !!vistoriaId,
          imageFiles,
          multipleImageFiles
        })
        const emailResult = await enviarEmailBaixaVistoria(emailPayload)
        if (!emailResult.success) {
          console.warn('Baixa salva, mas falha ao enviar e-mail:', emailResult.error)
          avisoEmail = emailResult.error || 'Não foi possível enviar o e-mail de confirmação.'
        }
      }
      
      // Mostrar modal de sucesso
      const mensagemSucesso = vistoriaId ? 'Baixa de vistoria atualizada com sucesso!' : 'Baixa de vistoria salva com sucesso!'
      setSuccessMessage(mensagemSucesso)
      setSuccessWarning(avisoEmail)
      setShowSuccessModal(true)
      closeBaixaModal()
      loadVistorias() // Recarregar lista
    } catch (error) {
      console.error('Erro ao salvar baixa de vistoria:', error)
      alert('Erro ao salvar baixa de vistoria: ' + error.message)
    } finally {
      setIsLoadingBaixa(false)
    }
  }

  // Carregar vistorias (filtrosOverride: ex. após Limpar, para não usar estado antigo do React)
  const loadVistorias = async (filtrosOverride) => {
    const f = filtrosOverride ?? filtros
    try {
      setIsLoadingVistorias(true)
      
      if (!userId) {
        console.error('Usuário não identificado')
        setVistorias([])
      return
    }

      // Primeiro, buscar o ID do tipo de serviço "Vistoria"
      const { data: tipoVistoria, error: tipoError } = await supabase
        .from('tipo_servico')
        .select('id')
        .eq('nome', 'Vistoria')
        .single()

      if (tipoError || !tipoVistoria) {
        console.error('Erro ao buscar tipo de serviço Vistoria:', tipoError)
        setVistorias([])
        return
      }

      // Buscar ordens de serviço do tipo Vistoria
      // Lógica de filtros baseada no role do usuário
      let ordensData = []
      
      if (
        userRole === 'administrador' ||
        userRole === 'homologador' ||
        (userRole === 'administrativo' && userEmpresa?.id === EMPRESA_ID_ADMINISTRATIVO_VER_TODAS_OS)
      ) {
        // Administrador, Homologador, ou Administrativo da empresa acima: todas as ordens (Vistoria)
        const { data, error } = await supabase
          .from('ordens_servicos')
          .select(`
            *,
            id_cliente,
            cliente:clientes_cadastro(id, nome_completo, cpf, telefone, email),
            tipo_servico:tipo_servico(id, nome),
            empresa:empresa(id, empresa),
            ordem_servico_responsaveis(
              id,
              id_responsavel,
              id_ordem_servico,
              usuario:usuarios_sistema(nome, email)
            )
          `)
          .eq('id_tipo_servico', tipoVistoria.id)
          .order('created_at', { ascending: false })
        
        if (error) throw error
        ordensData = data || []
      } else if (userRole === 'administrativo') {
        // Administrativo (outras empresas): ordens da empresa + instalações autorizadas
        if (userEmpresa?.id) {
          const { data: ordensEmpresa, error: errorEmpresa } = await supabase
            .from('ordens_servicos')
            .select(`
              *,
              id_cliente,
              cliente:clientes_cadastro(id, nome_completo, cpf, telefone, email),
              tipo_servico:tipo_servico(id, nome),
              empresa:empresa(id, empresa),
              ordem_servico_responsaveis(
                id,
                id_responsavel,
                id_ordem_servico,
                usuario:usuarios_sistema(nome, email)
              )
            `)
            .eq('id_tipo_servico', tipoVistoria.id)
            .eq('id_empresa', userEmpresa.id)
            .order('created_at', { ascending: false })

          if (errorEmpresa) throw errorEmpresa
          const ordensEmpresaList = ordensEmpresa || []
          console.log('📋 Ordens da empresa (Vistorias, administrativo restrito):', ordensEmpresaList.length)

          const { data: permissoes, error: errorPermissoes } = await supabase
            .from('permissoes_instalacoes')
            .select('instalacao_id')
            .eq('empresa_autorizada_id', userEmpresa.id)

          if (errorPermissoes) {
            console.error('❌ Erro ao buscar permissões:', errorPermissoes)
          }

          const instalacaoIdsPermitidos = (permissoes || []).map(p => p.instalacao_id)
          console.log('🔐 Instalações autorizadas (Vistorias, administrativo restrito):', instalacaoIdsPermitidos.length)

          if (instalacaoIdsPermitidos.length > 0) {
            const { data: ordensPermitidas, error: errorPermitidas } = await supabase
              .from('ordens_servicos')
              .select(`
                *,
                id_cliente,
                cliente:clientes_cadastro(id, nome_completo, cpf, telefone, email),
                tipo_servico:tipo_servico(id, nome),
                empresa:empresa(id, empresa),
                ordem_servico_responsaveis(
                  id,
                  id_responsavel,
                  id_ordem_servico,
                  usuario:usuarios_sistema(nome, email)
                )
              `)
              .eq('id_tipo_servico', tipoVistoria.id)
              .in('id_cliente', instalacaoIdsPermitidos)
              .neq('id_empresa', userEmpresa.id)
              .order('created_at', { ascending: false })

            if (errorPermitidas) {
              console.error('❌ Erro ao buscar ordens permitidas:', errorPermitidas)
            } else {
              const ordensPermitidasList = ordensPermitidas || []
              console.log('📋 Ordens permitidas (Vistorias, administrativo restrito):', ordensPermitidasList.length)

              const ordensMap = new Map()
              ordensEmpresaList.forEach(ordem => ordensMap.set(ordem.id, ordem))
              ordensPermitidasList.forEach(ordem => ordensMap.set(ordem.id, ordem))
              ordensData = Array.from(ordensMap.values()).sort((a, b) => {
                const dateA = new Date(a.created_at || 0)
                const dateB = new Date(b.created_at || 0)
                return dateB - dateA
              })
            }
          } else {
            ordensData = ordensEmpresaList
          }

          console.log('📋 Total (Vistorias, administrativo restrito):', ordensData.length)
        }
      } else if (userRole === 'avulso') {
        // Avulso: vê todas as vistorias da empresa do usuário (mesma lógica do administrativo)
        if (userEmpresa?.id) {
          // 1. Buscar ordens da empresa do usuário
          const { data: ordensEmpresa, error: errorEmpresa } = await supabase
            .from('ordens_servicos')
            .select(`
              *,
              id_cliente,
              cliente:clientes_cadastro(id, nome_completo, cpf, telefone, email),
              tipo_servico:tipo_servico(id, nome),
              empresa:empresa(id, empresa),
              ordem_servico_responsaveis(
                id,
                id_responsavel,
                id_ordem_servico,
                usuario:usuarios_sistema(nome, email)
              )
            `)
            .eq('id_tipo_servico', tipoVistoria.id)
            .eq('id_empresa', userEmpresa.id)
            .order('created_at', { ascending: false })

          if (errorEmpresa) throw errorEmpresa
          const ordensEmpresaList = ordensEmpresa || []
          console.log('📋 Ordens da empresa do usuário avulso (Vistorias):', ordensEmpresaList.length)

          // 2. Buscar permissões de instalações autorizadas
          const { data: permissoes, error: errorPermissoes } = await supabase
            .from('permissoes_instalacoes')
            .select('instalacao_id')
            .eq('empresa_autorizada_id', userEmpresa.id)

          if (errorPermissoes) {
            console.error('❌ Erro ao buscar permissões:', errorPermissoes)
          }

          const instalacaoIdsPermitidos = (permissoes || []).map(p => p.instalacao_id)
          console.log('🔐 Instalações autorizadas encontradas (Vistorias, avulso):', instalacaoIdsPermitidos.length)

          // 3. Buscar ordens das instalações autorizadas (que não sejam da própria empresa)
          if (instalacaoIdsPermitidos.length > 0) {
            const { data: ordensPermitidas, error: errorPermitidas } = await supabase
              .from('ordens_servicos')
              .select(`
                *,
                id_cliente,
                cliente:clientes_cadastro(id, nome_completo, cpf, telefone, email),
                tipo_servico:tipo_servico(id, nome),
                empresa:empresa(id, empresa),
                ordem_servico_responsaveis(
                  id,
                  id_responsavel,
                  id_ordem_servico,
                  usuario:usuarios_sistema(nome, email)
                )
              `)
              .eq('id_tipo_servico', tipoVistoria.id)
              .in('id_cliente', instalacaoIdsPermitidos)
              .neq('id_empresa', userEmpresa.id)
              .order('created_at', { ascending: false })

            if (errorPermitidas) {
              console.error('❌ Erro ao buscar ordens permitidas:', errorPermitidas)
            } else {
              const ordensPermitidasList = ordensPermitidas || []
              console.log('📋 Ordens permitidas encontradas (Vistorias, avulso):', ordensPermitidasList.length)

              const ordensMap = new Map()
              ordensEmpresaList.forEach(ordem => ordensMap.set(ordem.id, ordem))
              ordensPermitidasList.forEach(ordem => ordensMap.set(ordem.id, ordem))
              ordensData = Array.from(ordensMap.values()).sort((a, b) => {
                const dateA = new Date(a.created_at || 0)
                const dateB = new Date(b.created_at || 0)
                return dateB - dateA
              })
            }
          } else {
            ordensData = ordensEmpresaList
          }

          console.log('📋 Total de ordens encontradas (Vistorias, avulso):', ordensData.length)
        }
      } else if (userRole === 'vistoriador') {
        // Vistoriador: vê todas as vistorias onde é responsável, inclusive de outras empresas
        const { data: ordensVistoriador, error: errorVistoriador } = await supabase
          .from('ordens_servicos')
          .select(`
            *,
            id_cliente,
            cliente:clientes_cadastro(id, nome_completo, cpf, telefone, email),
            tipo_servico:tipo_servico(id, nome),
            empresa:empresa(id, empresa),
            ordem_servico_responsaveis!inner(
              id,
              id_responsavel,
              id_ordem_servico,
              usuario:usuarios_sistema(nome, email)
            )
          `)
          .eq('id_tipo_servico', tipoVistoria.id)
          .eq('ordem_servico_responsaveis.id_responsavel', userId)
          .order('created_at', { ascending: false })

        if (errorVistoriador) throw errorVistoriador
        ordensData = ordensVistoriador || []
      } else {
        // Campo e outros: veem apenas vistorias onde são responsáveis + com permissão
        if (userEmpresa?.id) {
          // 1. Buscar ordens da empresa onde o usuário é responsável
          const { data: ordensEmpresa, error: errorEmpresa } = await supabase
            .from('ordens_servicos')
            .select(`
              *,
              id_cliente,
              cliente:clientes_cadastro(id, nome_completo, cpf, telefone, email),
              tipo_servico:tipo_servico(id, nome),
              empresa:empresa(id, empresa),
              ordem_servico_responsaveis!inner(
                id,
                id_responsavel,
                id_ordem_servico,
                usuario:usuarios_sistema(nome, email)
              )
            `)
            .eq('id_tipo_servico', tipoVistoria.id)
            .eq('id_empresa', userEmpresa.id)
            .eq('ordem_servico_responsaveis.id_responsavel', userId)
            .order('created_at', { ascending: false })
          
          if (errorEmpresa) throw errorEmpresa
          const ordensEmpresaList = ordensEmpresa || []
          console.log('📋 Ordens da empresa onde usuário é responsável (Vistorias):', ordensEmpresaList.length)
          
          // 2. Buscar permissões de instalações autorizadas
          const { data: permissoes, error: errorPermissoes } = await supabase
            .from('permissoes_instalacoes')
            .select('instalacao_id')
            .eq('empresa_autorizada_id', userEmpresa.id)
          
          if (errorPermissoes) {
            console.error('❌ Erro ao buscar permissões:', errorPermissoes)
          }
          
          const instalacaoIdsPermitidos = (permissoes || []).map(p => p.instalacao_id)
          console.log('🔐 Instalações autorizadas encontradas (Vistorias):', instalacaoIdsPermitidos.length)
          
          // 3. Buscar ordens das instalações autorizadas onde o usuário também é responsável
          if (instalacaoIdsPermitidos.length > 0) {
            const { data: ordensPermitidas, error: errorPermitidas } = await supabase
              .from('ordens_servicos')
              .select(`
                *,
                id_cliente,
                cliente:clientes_cadastro(id, nome_completo, cpf, telefone, email),
                tipo_servico:tipo_servico(id, nome),
                empresa:empresa(id, empresa),
                ordem_servico_responsaveis!inner(
                  id,
                  id_responsavel,
                  id_ordem_servico,
                  usuario:usuarios_sistema(nome, email)
                )
              `)
              .eq('id_tipo_servico', tipoVistoria.id)
              .in('id_cliente', instalacaoIdsPermitidos)
              .neq('id_empresa', userEmpresa.id)
              .eq('ordem_servico_responsaveis.id_responsavel', userId)
              .order('created_at', { ascending: false })
            
            if (errorPermitidas) {
              console.error('❌ Erro ao buscar ordens permitidas:', errorPermitidas)
            } else {
              const ordensPermitidasList = ordensPermitidas || []
              console.log('📋 Ordens permitidas onde usuário é responsável (Vistorias):', ordensPermitidasList.length)
              
              // 4. Combinar ordens (usar Map para evitar duplicatas)
              const ordensMap = new Map()
              ordensEmpresaList.forEach(ordem => {
                ordensMap.set(ordem.id, ordem)
              })
              ordensPermitidasList.forEach(ordem => {
                ordensMap.set(ordem.id, ordem)
              })
              
              // Converter para array e ordenar por data
              ordensData = Array.from(ordensMap.values()).sort((a, b) => {
                const dateA = new Date(a.created_at || 0)
                const dateB = new Date(b.created_at || 0)
                return dateB - dateA
              })
            }
          } else {
            ordensData = ordensEmpresaList
          }
          
          console.log('📋 Total de ordens encontradas (Vistorias, com permissões):', ordensData.length)
        }
      }
      
      // Aplicar filtros adicionais aos dados já carregados
      // Nota: O filtro de status será aplicado após o merge com a tabela vistorias
      let ordensFiltradas = ordensData
      
      if (f.dataInicio) {
        ordensFiltradas = ordensFiltradas.filter(o => {
          if (!o.data_execucao) return false
          return o.data_execucao >= f.dataInicio
        })
      }
      if (f.dataFim) {
        ordensFiltradas = ordensFiltradas.filter(o => {
          if (!o.data_execucao) return false
          return o.data_execucao <= f.dataFim
        })
      }
      
      // Buscar vistorias cadastradas para obter o status correto
      const clienteIds = (ordensFiltradas || []).map(o => o.id_cliente).filter(Boolean)
      let vistoriasMap = new Map()
      
      if (clienteIds.length > 0) {
        const { data: vistoriasData, error: vistoriasError } = await supabase
          .from('vistorias')
          .select('id_cliente, status, data_vistoria')
          .in('id_cliente', clienteIds)
        
        if (!vistoriasError && vistoriasData) {
          // Criar mapa: id_cliente -> status da vistoria
          vistoriasData.forEach(v => {
            vistoriasMap.set(v.id_cliente, v.status)
          })
        }
      }
      
      // Buscar dados completos das vistorias para incluir no objeto
      const vistoriasCompletasMap = new Map()
      if (clienteIds.length > 0) {
        const { data: vistoriasCompletasData, error: vistoriasCompletasError } = await supabase
          .from('vistorias')
          .select(`
            *,
            tipo_padrao:tipo_padrao(id, nome)
          `)
          .in('id_cliente', clienteIds)
        
        if (!vistoriasCompletasError && vistoriasCompletasData) {
          // Criar mapa: id_cliente -> dados completos da vistoria
          vistoriasCompletasData.forEach(v => {
            vistoriasCompletasMap.set(v.id_cliente, v)
          })
        }
      }
      
      // Filtrar por nome do cliente se necessário (filtro client-side)
      // E usar o status da tabela vistorias se existir, senão usar o status de ordens_servicos
      let vistoriasFiltradas = (ordensFiltradas || []).map(v => ({
        ...v,
        // Usar status da vistoria se existir, senão usar status da ordem de serviço
        status: vistoriasMap.get(v.id_cliente) || v.status || 'Pendente',
        // Incluir dados completos da vistoria se existir
        vistoria_completa: vistoriasCompletasMap.get(v.id_cliente) || null,
        medicao_fechada: !!(vistoriasCompletasMap.get(v.id_cliente)?.medicao_fechada)
      }))
      
      // Filtro por nome do cliente é aplicado na UI (useMemo), para funcionar sem novo fetch e com Enter/busca normalizada.

      // Aplicar filtro de status (múltipla seleção)
      const statusSelecionados = Array.isArray(f.status) ? f.status : (f.status ? [f.status] : [])
      if (statusSelecionados.length > 0) {
        vistoriasFiltradas = vistoriasFiltradas.filter(v => {
          const s = (v.status || '').trim()
          return statusSelecionados.some(sel => s === sel || s.toLowerCase() === (sel || '').toLowerCase())
        })
      }
      
      setVistorias(vistoriasFiltradas)
    } catch (error) {
      console.error('Erro ao carregar vistorias:', error)
      alert('Erro ao carregar vistorias: ' + error.message)
    } finally {
      setIsLoadingVistorias(false)
    }
  }

  const vistoriasFiltradasCliente = useMemo(() => {
    const q = normalizeTextBuscaCliente(filtros.cliente)
    if (!q) return vistorias
    return vistorias.filter((v) => {
      const nome = normalizeTextBuscaCliente(getNomeClienteOrdem(v))
      return nome.includes(q)
    })
  }, [vistorias, filtros.cliente])

  // Contar vistorias por status
  const contarPorStatus = (status) => {
    return vistoriasFiltradasCliente.filter(v => v.status === status).length
  }

  // Exportar lista filtrada para Excel
  const exportarParaExcel = () => {
    const headers = [
      'ID',
      'Cliente',
      'CPF',
      'Telefone',
      'E-mail',
      'Status',
      'Data Criação',
      'Data Execução',
      'Empresa',
      'Responsáveis',
      'Observação Operacional'
    ]
    const rows = vistoriasFiltradasCliente.map((v) => {
      const responsaveis = (v.ordem_servico_responsaveis || [])
        .map((r) => r.usuario?.nome || r.usuario?.email || '')
        .filter(Boolean)
        .join('; ')
      return [
        v.id ?? '',
        v.cliente?.nome_completo ?? '',
        v.cliente?.cpf ?? '',
        v.cliente?.telefone ?? '',
        v.cliente?.email ?? '',
        v.status ?? '',
        v.created_at ? new Date(v.created_at).toLocaleDateString('pt-BR') : '',
        v.data_execucao ? new Date(v.data_execucao).toLocaleDateString('pt-BR') : '',
        (v.empresa && (v.empresa.empresa ?? v.empresa.nome)) ?? '',
        responsaveis,
        v.obs_operacional ?? ''
      ]
    })
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Vistorias')
    const nomeArquivo = `vistorias_${new Date().toISOString().slice(0, 10)}.xlsx`
    XLSX.writeFile(wb, nomeArquivo)
  }

  // Função auxiliar para baixar imagem e converter para base64
  const downloadImageAsBase64 = async (imageUrl) => {
    if (!imageUrl || !imageUrl.trim()) {
      console.warn('URL de imagem vazia')
      return null
    }
    
    try {
      const cleanUrl = imageUrl.trim()
      
      // Se for URL do Supabase Storage, tentar baixar diretamente
      if (cleanUrl.includes('supabase')) {
        // Extrair o caminho do arquivo da URL
        const urlParts = cleanUrl.split('/storage/v1/object/public/')
        if (urlParts.length > 1) {
          const pathParts = urlParts[1].split('/')
          const bucket = pathParts[0]
          const filePath = pathParts.slice(1).join('/')
          
          try {
            const { data, error } = await supabase.storage
              .from(bucket)
              .download(filePath)
            
            if (!error && data) {
              return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsDataURL(data)
              })
            } else {
              console.warn(`Erro ao baixar do Supabase Storage: ${error?.message || 'Erro desconhecido'}`)
            }
          } catch (storageError) {
            console.warn(`Erro ao acessar Supabase Storage: ${storageError.message}`)
          }
        }
      }
      
      // Fallback: tentar baixar via fetch (para URLs públicas)
      try {
        const response = await fetch(cleanUrl, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache'
        })
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        
        const blob = await response.blob()
        
        // Verificar se é uma imagem válida
        if (!blob.type.startsWith('image/')) {
          console.warn(`Arquivo não é uma imagem: ${blob.type}`)
          return null
        }
        
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = (error) => {
            console.error('Erro ao ler blob:', error)
            reject(error)
          }
          reader.readAsDataURL(blob)
        })
      } catch (fetchError) {
        console.warn(`Erro ao fazer fetch da imagem (${cleanUrl}):`, fetchError)
        return null
      }
    } catch (error) {
      console.error('Erro geral ao baixar imagem:', error)
      return null
    }
  }

  // Função auxiliar para parsear URLs de imagens (suporta arrays JSON)
  const parseImageUrls = (imageField) => {
    if (!imageField) return []
    
    // Se já é um array, retornar diretamente
    if (Array.isArray(imageField)) {
      return imageField.filter(url => url && url.trim())
    }
    
    // Se é string, tentar fazer parse JSON
    if (typeof imageField === 'string') {
      const trimmed = imageField.trim()
      
      // Se começa com [ ou {, tentar parse JSON
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) {
            return parsed.filter(url => url && url.trim())
          }
          return [parsed].filter(url => url && url.trim())
        } catch (e) {
          // Se falhar o parse, tratar como string simples
          return trimmed ? [trimmed] : []
        }
      }
      
      // Se contém vírgulas, pode ser uma lista separada por vírgulas
      if (trimmed.includes(',')) {
        return trimmed.split(',').map(url => url.trim()).filter(url => url)
      }
      
      // String simples
      return trimmed ? [trimmed] : []
    }
    
    // Outros tipos, converter para array
    return [imageField].filter(url => url && url.trim())
  }

  // Gerar relatório PDF da vistoria
  const gerarRelatorioPDF = async (vistoria) => {
    try {
      // Abrir modal de loading
      setShowPdfLoadingModal(true)
      
      // Buscar dados completos da vistoria se não estiverem carregados
      let vistoriaCompleta = vistoria.vistoria_completa
      const idCliente = vistoria.id_cliente || vistoria.cliente?.id
      
      if (!vistoriaCompleta && idCliente) {
        const { data: vistoriaData, error: vistoriaError } = await supabase
          .from('vistorias')
          .select(`
            *,
            tipo_padrao:tipo_padrao(id, nome)
          `)
          .eq('id_cliente', idCliente)
          .order('data_criacao', { ascending: false })
          .limit(1)
          .maybeSingle()
        
        if (!vistoriaError && vistoriaData) {
          vistoriaCompleta = vistoriaData
        }
      }

      // Buscar nome do tipo de padrão se necessário
      if (vistoriaCompleta && vistoriaCompleta.tipo_padrao_id && !vistoriaCompleta.tipo_padrao) {
        const { data: tipoPadraoData } = await supabase
          .from('tipo_padrao')
          .select('nome')
          .eq('id', vistoriaCompleta.tipo_padrao_id)
          .single()
        
        if (tipoPadraoData) {
          vistoriaCompleta.tipo_padrao = tipoPadraoData
        }
      }

      // Criar PDF
      const doc = new jsPDF('p', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 20
      const contentWidth = pageWidth - (margin * 2)
      let yPosition = margin

      // Cores elegantes
      const primaryColor = [41, 128, 185] // Azul elegante
      const accentColor = [52, 152, 219] // Azul claro
      const secondaryColor = [108, 117, 125]
      const textColor = [33, 37, 41]
      const lightGray = [248, 249, 250]
      const borderColor = [220, 221, 222]

      // Função auxiliar para adicionar nova página se necessário
      const checkNewPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - 30) {
          doc.addPage()
          yPosition = margin
          return true
        }
        return false
      }

      // Função auxiliar para sanitizar texto
      const sanitizeText = (text) => {
        if (!text) return 'N/A'
        return String(text).replace(/[^\x20-\x7E\u00A0-\u024F\u1E00-\u1EFF]/g, '')
      }

      // Função para adicionar seção com título elegante
      const addSectionTitle = (title) => {
        checkNewPage(25)
        
        // Fundo colorido para o título
        doc.setFillColor(...lightGray)
        doc.roundedRect(margin, yPosition - 5, contentWidth, 12, 2, 2, 'F')
        
        // Linha decorativa
        doc.setFillColor(...primaryColor)
        doc.roundedRect(margin, yPosition - 5, 4, 12, 1, 1, 'F')
        
        doc.setFontSize(13)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(...primaryColor)
        doc.text(title, margin + 8, yPosition + 3)
        
        yPosition += 12
      }

      // Função para adicionar campo com label
      const addField = (label, value, isBold = false) => {
        if (!value || value === 'N/A') return
        
        doc.setFontSize(10)
        const lineHeight = 6
        
        // Estrutura de duas colunas: label fixo e valor flexível
        const labelColumnWidth = 65 // mm - largura fixa para a coluna de labels
        const labelValueSpacing = 8 // mm - espaçamento entre label e valor (equivalente a ~20px)
        const valueStartX = margin + labelColumnWidth + labelValueSpacing
        const valueMaxWidth = pageWidth - valueStartX - margin
        
        const valueText = sanitizeText(String(value))
        const labelText = `${label}:`
        
        // Calcular linhas do label (se necessário)
        doc.setFont('helvetica', 'bold')
        const labelMaxWidth = labelColumnWidth - 2
        const labelLines = doc.splitTextToSize(labelText, labelMaxWidth)
        
        // Calcular linhas do valor
        doc.setFont('helvetica', 'normal')
        const valueLines = doc.splitTextToSize(valueText, valueMaxWidth)
        
        // Calcular espaço necessário
        const maxLines = Math.max(labelLines.length, valueLines.length)
        const requiredSpace = 10 + (maxLines * lineHeight)
        checkNewPage(requiredSpace)
        
        const startY = yPosition
        
        // Desenhar label(s) na coluna esquerda
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(...textColor)
        labelLines.forEach((line, index) => {
          doc.text(line, margin, startY + (index * lineHeight))
        })
        
        // Desenhar valor(s) na coluna direita
        doc.setFont('helvetica', isBold ? 'bold' : 'normal')
        doc.setTextColor(...secondaryColor)
        valueLines.forEach((line, index) => {
          doc.text(line, valueStartX, startY + (index * lineHeight))
        })
        
        // Atualizar yPosition para o próximo campo
        yPosition = startY + (maxLines * lineHeight) + 4
      }

      // Função para adicionar imagem ao PDF
      const addImage = async (imageUrl, caption, width = 80) => {
        if (!imageUrl || !imageUrl.trim()) {
          console.warn(`URL de imagem vazia ou inválida para: ${caption}`)
          return false
        }
        
        try {
          checkNewPage(width + 20)
          
          // Limpar URL de espaços e caracteres inválidos
          const cleanUrl = imageUrl.trim()
          
          const imageData = await downloadImageAsBase64(cleanUrl)
          if (!imageData) {
            console.warn(`Falha ao baixar imagem para ${caption}: ${cleanUrl}`)
            return false
          }
          
          // Calcular posição centralizada
          const xPosition = (pageWidth - width) / 2
          
          // Adicionar imagem
          doc.addImage(imageData, 'JPEG', xPosition, yPosition, width, width * 0.75)
          
          // Adicionar legenda
          yPosition += (width * 0.75) + 5
          doc.setFontSize(9)
          doc.setFont('helvetica', 'italic')
          doc.setTextColor(...secondaryColor)
          doc.text(caption, pageWidth / 2, yPosition, { align: 'center' })
          
          yPosition += 8
          console.log(`Imagem adicionada com sucesso: ${caption}`)
          return true
        } catch (error) {
          console.error(`Erro ao adicionar imagem ${caption} (URL: ${imageUrl}):`, error)
          return false
        }
      }

      // Cabeçalho elegante
      doc.setFillColor(...primaryColor)
      doc.rect(0, 0, pageWidth, 50, 'F')
      
      // Gradiente visual
      doc.setFillColor(...accentColor)
      doc.rect(0, 0, pageWidth, 5, 'F')
      
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(22)
      doc.setFont('helvetica', 'bold')
      doc.text('RELATÓRIO DE VISTORIA TÉCNICA', pageWidth / 2, 25, { align: 'center' })
      
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.text('Sistema de Gestão de Vistorias - ConectSolar', pageWidth / 2, 35, { align: 'center' })
      
      // Data do relatório
      const now = new Date()
      doc.setFontSize(9)
      doc.setTextColor(255, 255, 255, 0.8)
      doc.text(`Gerado em: ${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR')}`, pageWidth / 2, 43, { align: 'center' })
      
      yPosition = 60

      // ========== INFORMAÇÕES DO CLIENTE ==========
      addSectionTitle('INFORMAÇÕES DO CLIENTE')
      
      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      
      addField('Nome Completo', vistoria.cliente?.nome_completo, true)
      addField('CPF', vistoria.cliente?.cpf)
      addField('Telefone', vistoria.cliente?.telefone)
      addField('E-mail', vistoria.cliente?.email)
      
      yPosition += 5

      // ========== INFORMAÇÕES DA ORDEM DE SERVIÇO ==========
      addSectionTitle('INFORMAÇÕES DA ORDEM DE SERVIÇO')
      
      addField('ID da Ordem', vistoria.id ? String(vistoria.id).substring(0, 8) + '...' : 'N/A')
      addField('Empresa', vistoria.empresa?.empresa)
      addField('Data de Execução', vistoria.data_execucao ? new Date(vistoria.data_execucao).toLocaleDateString('pt-BR') : 'N/A')
      addField('Data de Criação', vistoria.created_at ? new Date(vistoria.created_at).toLocaleDateString('pt-BR') : 'N/A')
      addField('Status', vistoria.status)
      
      yPosition += 5

        // ========== DADOS DA VISTORIA ==========
        if (vistoriaCompleta) {
          addSectionTitle('DADOS TÉCNICOS DA VISTORIA')
        
        // Tipo de Padrão
        let tipoPadraoNome = vistoriaCompleta.tipo_padrao?.nome
        if (!tipoPadraoNome && vistoriaCompleta.tipo_padrao_id) {
          const { data: tipoPadraoData } = await supabase
            .from('tipo_padrao')
            .select('nome')
            .eq('id', vistoriaCompleta.tipo_padrao_id)
            .single()
          if (tipoPadraoData) tipoPadraoNome = tipoPadraoData.nome
        }
        addField('Tipo de Padrão', tipoPadraoNome)
        addField('Ligação do Padrão', vistoriaCompleta.ligacao_padrao)
        addField('Data da Vistoria', vistoriaCompleta.data_vistoria ? new Date(vistoriaCompleta.data_vistoria).toLocaleDateString('pt-BR') : 'N/A')
        
        // Parecer Final com destaque de cor
        const parecerFinal = vistoriaCompleta.status || 'N/A'
        const parecerFinalUpper = String(parecerFinal).toUpperCase()
        
        // Determinar cor baseada no status
        let corParecer = textColor
        if (parecerFinalUpper.includes('APROVADO') && parecerFinalUpper.includes('OBRA')) {
          corParecer = [255, 152, 0] // Laranja para "Aprovado com Obra"
        } else if (parecerFinalUpper.includes('APROVADO')) {
          corParecer = [39, 174, 96] // Verde para "Aprovado"
        } else if (parecerFinalUpper.includes('REPROVADO')) {
          corParecer = [231, 76, 60] // Vermelho para "Reprovado"
        }
        
        // Adicionar campo com cor destacada
        if (parecerFinal && parecerFinal !== 'N/A') {
          const labelColumnWidth = 65
          const labelValueSpacing = 8
          const valueStartX = margin + labelColumnWidth + labelValueSpacing
          const valueMaxWidth = pageWidth - valueStartX - margin
          const lineHeight = 6
          
          const labelText = 'Parecer Final:'
          const valueText = sanitizeText(String(parecerFinal))
          
          doc.setFont('helvetica', 'bold')
          const labelLines = doc.splitTextToSize(labelText, labelColumnWidth - 2)
          doc.setFont('helvetica', 'bold')
          const valueLines = doc.splitTextToSize(valueText, valueMaxWidth)
          
          const maxLines = Math.max(labelLines.length, valueLines.length)
          const requiredSpace = 10 + (maxLines * lineHeight)
          checkNewPage(requiredSpace)
          
          const startY = yPosition
          
          // Desenhar label
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(...textColor)
          labelLines.forEach((line, index) => {
            doc.text(line, margin, startY + (index * lineHeight))
          })
          
          // Desenhar valor com cor destacada
          doc.setFont('helvetica', 'bold')
          doc.setTextColor(...corParecer)
          valueLines.forEach((line, index) => {
            doc.text(line, valueStartX, startY + (index * lineHeight))
          })
          
          yPosition = startY + (maxLines * lineHeight) + 4
        } else {
          addField('Parecer Final', parecerFinal, true)
        }
        
        yPosition += 5

        // ========== LOCAIS DE INSTALAÇÃO ==========
        addSectionTitle('LOCAIS DE INSTALAÇÃO')
        
        addField('Local do Inversor', vistoriaCompleta.local_inversor)
        addField('Posição do Poste da Concessionária', vistoriaCompleta.posicao_poste_concessionaria)
        addField('Conexão do Inversor', vistoriaCompleta.conexao_inversor)
        addField('Local de Aterramento do Sistema', vistoriaCompleta.local_aterramento_sistema)
        
        yPosition += 5

        // ========== METRAGENS ==========
        addSectionTitle('METRAGENS E DIMENSÕES (Percurso)')
        
        if (vistoriaCompleta.metragem_ca_inversor) {
          addField('C.A. Inversor', `${vistoriaCompleta.metragem_ca_inversor} metros`)
        }
        if (vistoriaCompleta.metragem_cc) {
          addField('C.C. (Cabos Solares)', `${vistoriaCompleta.metragem_cc} metros`)
        }
        if (vistoriaCompleta.metragem_ca_padrao_fases) {
          addField('C.A. Padrão - Fases', `${vistoriaCompleta.metragem_ca_padrao_fases} metros`)
        }
        if (vistoriaCompleta.metragem_ca_padrao_neutros) {
          addField('C.A. Padrão - Neutros', `${vistoriaCompleta.metragem_ca_padrao_neutros} metros`)
        }
        if (vistoriaCompleta.percurso_aterramento) {
          addField('Percurso Aterramento', `${vistoriaCompleta.percurso_aterramento} metros`)
        }
        
        yPosition += 5

        // ========== QUANTIDADES DE MATERIAIS ==========
        addSectionTitle('QUANTIDADES DE MATERIAIS')
        
        if (vistoriaCompleta.qtd_conduletes_inversor) {
          addField('Conduletes para Inversor', `${vistoriaCompleta.qtd_conduletes_inversor} unidades`)
        }
        if (vistoriaCompleta.qtd_eletroduto_inversor) {
          addField('Eletroduto até o Telhado', `${vistoriaCompleta.qtd_eletroduto_inversor} unidades`)
        }
        if (vistoriaCompleta.qtd_eletroduto_padrao) {
          addField('Eletroduto para Padrão', `${vistoriaCompleta.qtd_eletroduto_padrao} unidades`)
        }
        if (vistoriaCompleta.qtd_modulos_telhado) {
          addField('Quantos módulos comportam no telhado', `${vistoriaCompleta.qtd_modulos_telhado} módulos`)
        }
        
        yPosition += 5

        // ========== FOTOS DA VISTORIA ==========
        addSectionTitle('REGISTRO FOTOGRÁFICO')
        
        // Lista de todas as imagens a serem processadas
        const imagensParaProcessar = [
          { campo: 'caminho_img_padrao', label: 'Foto do Padrão' },
          { campo: 'caminho_img_ramal', label: 'Foto do Ramal de Entrada' },
          { campo: 'caminho_img_inversor', label: 'Foto de onde será instalado o Inversor' },
          { campo: 'caminho_img_fachada', label: 'Foto da Fachada' },
          { campo: 'caminho_img_mapa', label: 'Print do Mapa' },
          { campo: 'caminho_img_telhado', label: 'Foto do Telhado' },
          { campo: 'caminho_img_quadro', label: 'Foto do Quadro de Distribuição' },
          { campo: 'caminho_img_estrutura', label: 'Foto da Estrutura' },
          { campo: 'caminho_img_croqui', label: 'Foto do Croqui' }
        ]
        
        // Processar todas as imagens
        for (const imagemInfo of imagensParaProcessar) {
          const campoImagem = vistoriaCompleta[imagemInfo.campo]
          if (campoImagem) {
            const urls = parseImageUrls(campoImagem)
            console.log(`Processando ${imagemInfo.label}: ${urls.length} imagem(ns) encontrada(s)`)
            
            for (let i = 0; i < urls.length; i++) {
              const sufixo = urls.length > 1 ? ` (${i + 1}/${urls.length})` : ''
              const sucesso = await addImage(urls[i], `${imagemInfo.label}${sufixo}`, 80)
              if (!sucesso) {
                console.warn(`Falha ao adicionar ${imagemInfo.label}${sufixo}`)
              }
            }
          }
        }
        
        yPosition += 5

        // ========== OBSERVAÇÕES ==========
        if (vistoriaCompleta.observacoes) {
          addSectionTitle('OBSERVAÇÕES E ANOTAÇÕES')
          
          doc.setFontSize(10)
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(...textColor)
          
          // Preservar quebras de linha: dividir por \n e \r\n
          const observacoes = String(vistoriaCompleta.observacoes || '')
          const linhasOriginais = observacoes.split(/\r?\n/)
          const maxWidth = contentWidth - 10
          const lineHeight = 5
          const padding = 10
          
          // Processar cada linha mantendo as quebras originais
          const todasLinhas = []
          linhasOriginais.forEach((linha) => {
            // Limpar apenas caracteres problemáticos, mantendo acentuação e espaços
            const linhaLimpa = linha.replace(/[^\x20-\x7E\u00A0-\u024F\u1E00-\u1EFF]/g, '')
            if (linhaLimpa.trim()) {
              // Quebrar linha longa se necessário
              const linhasQuebradas = doc.splitTextToSize(linhaLimpa, maxWidth)
              todasLinhas.push(...linhasQuebradas)
            } else {
              // Preservar linha vazia (quebra de linha)
              todasLinhas.push('')
            }
          })
          
          // Calcular altura total necessária
          const alturaTotal = todasLinhas.length * lineHeight + padding
          checkNewPage(alturaTotal + 10)
          
          const startY = yPosition
          let currentY = startY + 5
          
          // Desenhar fundo
          doc.setFillColor(...lightGray)
          doc.roundedRect(margin, startY - 5, contentWidth, alturaTotal, 3, 3, 'F')
          
          // Adicionar cada linha preservando quebras
          todasLinhas.forEach((linha, index) => {
            // Verificar espaço antes de adicionar linha
            if (currentY + lineHeight > pageHeight - margin - 20) {
              doc.addPage()
              currentY = margin + 5
              yPosition = currentY
              // Redesenhar fundo se necessário
              const linhasRestantes = todasLinhas.length - index
              const alturaRestante = linhasRestantes * lineHeight + padding
              doc.setFillColor(...lightGray)
              doc.roundedRect(margin, currentY - 5, contentWidth, alturaRestante, 3, 3, 'F')
            }
            
            // Adicionar texto da linha
            doc.text(linha || ' ', margin + 5, currentY)
            currentY += lineHeight
            yPosition = currentY
          })
          
          yPosition += 5
        }
      } else {
        addSectionTitle('STATUS DA VISTORIA')
        
        checkNewPage(15)
        doc.setFontSize(11)
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(...secondaryColor)
        doc.text('Esta vistoria ainda não foi preenchida. Os dados técnicos serão', margin, yPosition)
        yPosition += 7
        doc.text('incluídos no relatório após o preenchimento da baixa de vistoria.', margin, yPosition)
        yPosition += 10
      }

      // ========== RODAPÉ ==========
      const totalPages = doc.internal.pages.length - 1
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        
        // Linha decorativa do rodapé
        doc.setDrawColor(...borderColor)
        doc.setLineWidth(0.5)
        doc.line(margin, pageHeight - 25, pageWidth - margin, pageHeight - 25)
        
        // Informações do rodapé
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...secondaryColor)
        doc.text(`Página ${i} de ${totalPages}`, pageWidth / 2, pageHeight - 15, { align: 'center' })
        doc.text('Documento gerado automaticamente pelo Sistema ConectSolar', pageWidth / 2, pageHeight - 10, { align: 'center' })
      }

      // Fechar modal de loading antes de iniciar o download
      setShowPdfLoadingModal(false)
      
      // Salvar PDF
      const clienteNome = (vistoria.cliente?.nome_completo || 'Vistoria').replace(/[^a-zA-Z0-9]/g, '_')
      const fileName = `Relatorio_Vistoria_${clienteNome}_${Date.now()}.pdf`
      doc.save(fileName)
    } catch (error) {
      console.error('Erro ao gerar relatório PDF:', error)
      setShowPdfLoadingModal(false)
      alert('Erro ao gerar relatório PDF: ' + error.message)
    }
  }

  const statusBadgeClass = (status) => {
    const statusMap = {
      'pendente': 'badge-warning',
      'em_andamento': 'badge-info',
      'finalizado': 'badge-success',
      'cancelado': 'badge-danger',
      'Pendente': 'badge-warning',
      'Em andamento': 'badge-info',
      'Concluida': 'badge-success',
      'Revisado': 'badge-primary',
      'Cancelada': 'badge-danger',
      'Novo': 'badge-success',
      'Aprovado': 'badge-success',
      'Aprovado com Obra': 'badge-primary',
      'Cancelado': 'badge-danger',
      'Reprovado': 'badge-danger'
    }
    return statusMap[status] || 'badge-secondary'
  }

  const openDetalhesVistoria = async (vistoria) => {
    let vistoriaCompleta = vistoria.vistoria_completa || null
    const idCliente = vistoria.id_cliente || vistoria.cliente?.id
    if (!vistoriaCompleta && idCliente) {
      const { data: vistoriaData, error: vistoriaError } = await supabase
        .from('vistorias')
        .select(`*, tipo_padrao:tipo_padrao(id, nome)`)
        .eq('id_cliente', idCliente)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (!vistoriaError && vistoriaData) vistoriaCompleta = vistoriaData
    }
    setVistoriaDetalhes({ ...vistoria, vistoria_completa: vistoriaCompleta })
    setShowDetalhesModal(true)
  }

  return (
    <div className="menu-content">
      <div className="menu-header">
        <h2>📋 Gerenciar Vistorias</h2>
        <button className="back-button" onClick={onBack}>← Voltar</button>
      </div>

      {/* Cards de Status */}
      <div className="status-cards">
        <div className="status-card">
          <h4>Pendente</h4>
          <span className="status-count">{contarPorStatus('pendente') + contarPorStatus('Pendente')}</span>
        </div>
        <div className="status-card">
          <h4>Em andamento</h4>
          <span className="status-count">{contarPorStatus('em_andamento') + contarPorStatus('Em andamento')}</span>
        </div>
        <div className="status-card">
          <h4>Finalizado</h4>
          <span className="status-count">{contarPorStatus('finalizado') + contarPorStatus('Concluida')}</span>
        </div>
        <div className="status-card">
          <h4>Cancelado</h4>
          <span className="status-count">{contarPorStatus('cancelado') + contarPorStatus('Cancelada')}</span>
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="vistoria-actions">
        <button
          className="action-button secondary"
          onClick={() => {
            const merged = { ...filtros, cliente: buscaClienteInput.trim() }
            setFiltros(merged)
            loadVistorias(merged)
          }}
          disabled={isLoadingVistorias}
          title="Carregar vistorias da tabela ordens_servicos"
        >
          🔄 Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div className="filter-section">
        <h4>🔍 Filtros</h4>
        <div className="filter-row">
          <div className="filter-fields">
            <input
              type="text"
              placeholder="Buscar por cliente... (Enter para pesquisar)"
              value={buscaClienteInput}
              onChange={(e) => setBuscaClienteInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                e.preventDefault()
                const merged = { ...filtros, cliente: buscaClienteInput.trim() }
                setFiltros(merged)
              }}
              className="filter-input"
              aria-label="Buscar por nome do cliente"
            />
            <div className="filter-status-dropdown" ref={statusDropdownRef}>
              <button
                type="button"
                className="filter-status-dropdown-trigger"
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                aria-expanded={showStatusDropdown}
                aria-haspopup="listbox"
              >
                <span className="filter-status-dropdown-label">Status</span>
                <span className="filter-status-dropdown-value">
                  {Array.isArray(filtros.status) && filtros.status.length > 0
                    ? `${filtros.status.length} selecionado(s)`
                    : 'Todos'}
                </span>
                <span className="filter-status-dropdown-arrow">{showStatusDropdown ? '▲' : '▼'}</span>
              </button>
              {showStatusDropdown && (
                <div className="filter-status-dropdown-panel" role="listbox">
                  {OPCOES_STATUS_VISTORIA.map((opcao) => {
                    const statusArr = Array.isArray(filtros.status) ? filtros.status : []
                    const checked = statusArr.some(s => (s || '').toLowerCase() === (opcao || '').toLowerCase())
                    return (
                      <label key={opcao} className="filter-status-dropdown-item" role="option">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? statusArr.filter(s => (s || '').toLowerCase() !== (opcao || '').toLowerCase())
                              : [...statusArr, opcao]
                            setFiltros({ ...filtros, status: next })
                          }}
                        />
                        <span>{opcao}</span>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
            <input
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })}
              className="filter-input"
            />
            <input
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })}
              className="filter-input"
            />
          </div>

          <div className="filter-actions">
            <button
              onClick={() => {
                const merged = { ...filtros, cliente: buscaClienteInput.trim() }
                setFiltros(merged)
                loadVistorias(merged)
              }}
              className="filter-button"
              disabled={isLoadingVistorias}
            >
              🔍 Aplicar Filtros
            </button>
            <button
              onClick={() => {
                const reset = {
                  cliente: '',
                  status: userRole === 'vistoriador' ? ['Novo', 'Em andamento'] : [],
                  dataInicio: '',
                  dataFim: ''
                }
                setBuscaClienteInput('')
                setFiltros(reset)
                loadVistorias(reset)
              }}
              className="filter-button-reset"
            >
              ✕ Limpar
            </button>
            <button
              type="button"
              onClick={exportarParaExcel}
              className="filter-button filter-button-export"
              disabled={vistoriasFiltradasCliente.length === 0}
              title={vistoriasFiltradasCliente.length === 0 ? 'Nenhuma vistoria para exportar' : 'Exportar lista filtrada para Excel'}
            >
              📥 Exportar Excel
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de Vistorias (desktop) / Cards (mobile) */}
      <div className="table-section">
        <h4>📊 Vistorias Cadastradas</h4>
        {isLoadingVistorias ? (
          <p className="loading-text">Carregando vistorias...</p>
        ) : vistorias.length === 0 ? (
          <p className="empty-text">Nenhuma vistoria cadastrada</p>
        ) : vistoriasFiltradasCliente.length === 0 ? (
          <p className="empty-text">Nenhuma vistoria encontrada para este cliente.</p>
        ) : isMobile ? (
          <div className="vistoria-cards">
            {vistoriasFiltradasCliente.map(vistoria => (
              <article key={vistoria.id} className="vistoria-card">
                <header className="vistoria-card-header">
                  <span className="vistoria-card-id">#{String(vistoria.id || '').substring(0, 8)}...</span>
                  <span className={`badge ${statusBadgeClass(vistoria.status)}`}>
                    {vistoria.status || 'N/A'}
                  </span>
                  {vistoriaMedicaoFechada(vistoria) && (
                    <span className="badge bg-secondary" title="Medição fechada em Serviços" style={{ marginLeft: '6px' }}>🔒 Medição fechada</span>
                  )}
                </header>
                <div className="vistoria-card-body">
                  <button
                    type="button"
                    className="vistoria-card-cliente"
                    onClick={() => {
                      const statusNormalizado = vistoria.status?.toLowerCase()
                      if (statusNormalizado === 'novo') openEscolhaModal(vistoria)
                      else openBaixaModal(vistoria)
                    }}
                    title={vistoria.status?.toLowerCase() === 'novo'
                      ? 'Escolher: Localização ou Baixar Vistoria'
                      : (vistoriaMedicaoFechada(vistoria) ? 'Medição fechada — consultar baixa' : 'Preencher ou editar baixa')}
                  >
                    {vistoria.cliente?.nome_completo || 'N/A'}
                  </button>
                  <p className="vistoria-card-empresa">{vistoria.empresa?.empresa || 'N/A'}</p>
                </div>
                <div className="vistoria-card-dates">
                  <span className="vistoria-card-date">
                    <span className="vistoria-card-date-icon" aria-hidden>📅</span>
                    Criado: {vistoria.created_at ? new Date(vistoria.created_at).toLocaleDateString('pt-BR') : '-'}
                  </span>
                  <span className="vistoria-card-date">
                    <span className="vistoria-card-date-icon" aria-hidden>📅</span>
                    Exec: {vistoria.data_execucao ? new Date(vistoria.data_execucao).toLocaleDateString('pt-BR') : '-'}
                  </span>
                </div>
                <footer className="vistoria-card-footer">
                  <button
                    type="button"
                    className="vistoria-card-btn vistoria-card-btn-view"
                    onClick={() => openDetalhesVistoria(vistoria)}
                    title="Ver Detalhes"
                  >
                    Visualizar
                  </button>
                  <button
                    type="button"
                    className="vistoria-card-btn vistoria-card-btn-pdf"
                    onClick={() => gerarRelatorioPDF(vistoria)}
                    title="Gerar Relatório PDF"
                  >
                    PDF
                  </button>
                  <button
                    type="button"
                    className="vistoria-card-btn vistoria-card-btn-info-cliente"
                    onClick={() => openInfoClienteModal(vistoria)}
                    title="Endereço e kit solar do cliente"
                  >
                    Informações do Cliente
                  </button>
                </footer>
                {(vistoria.status === 'Novo' || (vistoria.status && vistoria.status.toLowerCase() === 'novo')) && (
                  <div className="vistoria-card-action-primary">
                    <button
                      type="button"
                      className="vistoria-card-btn-primary"
                      onClick={() => openEscolhaModal(vistoria)}
                      title="Iniciar Vistoria"
                    >
                      Iniciar Vistoria
                    </button>
                  </div>
                )}
                {(vistoria.status === 'Em andamento' || (vistoria.status && vistoria.status.trim().toLowerCase() === 'em andamento')) && (
                  <div className="vistoria-card-action-primary">
                    <button
                      type="button"
                      className="vistoria-card-btn-primary"
                      onClick={() => openBaixaModal(vistoria)}
                      title={vistoriaMedicaoFechada(vistoria) ? 'Medição fechada — consultar baixa' : 'Baixar Vistoria'}
                    >
                      {vistoriaMedicaoFechada(vistoria) ? 'Consultar baixa' : 'Baixar Vistoria'}
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="vistoria-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Cliente</th>
                  <th>Status</th>
                  <th>Data Execução</th>
                  <th>Data Criação</th>
                  <th>Empresa</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {vistoriasFiltradasCliente.map(vistoria => (
                  <tr key={vistoria.id} className="table-row">
                    <td className="table-id">#{String(vistoria.id || '').substring(0, 8)}...</td>
                    <td 
                      className="table-cliente"
                      style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                      onClick={() => {
                        const statusNormalizado = vistoria.status?.toLowerCase()
                        if (statusNormalizado === 'novo') {
                          openEscolhaModal(vistoria)
                        } else {
                          openBaixaModal(vistoria)
                        }
                      }}
                      title={vistoria.status?.toLowerCase() === 'novo'
                        ? "Clique para escolher: Localização ou Baixar Vistoria"
                        : (vistoriaMedicaoFechada(vistoria)
                          ? "Medição fechada — clique para consultar (sem editar)"
                          : "Clique para preencher ou editar a baixa da vistoria")}
                    >
                      {vistoria.cliente?.nome_completo || 'N/A'}
                    </td>
                    <td className="table-status">
                      <span className={`badge ${statusBadgeClass(vistoria.status)}`}>
                        {vistoria.status || 'N/A'}
                      </span>
                      {vistoriaMedicaoFechada(vistoria) && (
                        <span className="badge bg-secondary" title="Medição fechada em Serviços" style={{ marginLeft: '6px' }}>🔒 Fechada</span>
                      )}
                    </td>
                    <td className="table-date">
                      {vistoria.data_execucao ? new Date(vistoria.data_execucao).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="table-date">
                      {vistoria.created_at ? new Date(vistoria.created_at).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="table-empresa">
                      {vistoria.empresa?.empresa || 'N/A'}
                    </td>
                    <td className="table-actions">
                      <button
                        className="btn-view"
                        onClick={() => openDetalhesVistoria(vistoria)}
                        title="Ver Detalhes"
                      >
                        👁
                      </button>
                      <button
                        className="btn-view"
                        onClick={() => gerarRelatorioPDF(vistoria)}
                        title="Gerar Relatório PDF"
                        style={{ marginLeft: '5px' }}
                      >
                        📄
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Escolha (Localização ou Baixar Vistoria) */}
      {showEscolhaModal && vistoriaParaEscolha && createPortal(
        <div className="modal-overlay" onClick={closeEscolhaModal}>
          <div className="modal-content" style={{ maxWidth: '500px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📍 {vistoriaParaEscolha.cliente?.nome_completo || 'Cliente'}</h3>
              <button className="modal-close" onClick={closeEscolhaModal}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '30px', textAlign: 'center' }}>
              <p style={{ marginBottom: '30px', fontSize: '16px', color: '#555' }}>
                O que deseja fazer?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <button
                  className="btn-primary"
                  style={{
                    padding: '15px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: '#007bff',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    transition: 'all 0.3s'
                  }}
                  onClick={async () => {
                    closeEscolhaModal()
                    await openBaixaModal(vistoriaParaEscolha)
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                >
                  📝 Baixar Vistoria
                </button>
                <button
                  className="btn-secondary"
                  style={{
                    padding: '15px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    transition: 'all 0.3s'
                  }}
                  onClick={handleAbrirInformacoesCliente}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  📋 Informações do Cliente
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Informações do Cliente (Endereço e Kit) */}
      {showInfoClienteModal && infoCliente && createPortal(
        <div className="modal-overlay" onClick={closeInfoClienteModal}>
          <div className="modal-content" style={{ maxWidth: '700px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📋 Informações do Cliente - {infoCliente.nome_completo || 'N/A'}</h3>
              <button className="modal-close" onClick={closeInfoClienteModal}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '30px' }}>
              {/* Informações Pessoais */}
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '8px' }}>
                  👤 Dados Pessoais
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {infoCliente.nome_completo && (
                    <div>
                      <strong>Nome Completo:</strong>
                      <p style={{ margin: '5px 0', color: '#555' }}>{infoCliente.nome_completo}</p>
                    </div>
                  )}
                  {infoCliente.cpf && (
                    <div>
                      <strong>CPF:</strong>
                      <p style={{ margin: '5px 0', color: '#555' }}>{infoCliente.cpf}</p>
                    </div>
                  )}
                  {infoCliente.telefone && (
                    <div>
                      <strong>Telefone:</strong>
                      <p style={{ margin: '5px 0', color: '#555' }}>{infoCliente.telefone}</p>
                    </div>
                  )}
                  {infoCliente.email && (
                    <div>
                      <strong>E-mail:</strong>
                      <p style={{ margin: '5px 0', color: '#555' }}>{infoCliente.email}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Endereço */}
              <div style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #28a745', paddingBottom: '8px' }}>
                  📍 Endereço
                </h4>
                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                  {infoCliente.logradouro || infoCliente.numero || infoCliente.bairro || infoCliente.cidade || infoCliente.estado || infoCliente.cep ? (
                    <p style={{ margin: '5px 0', lineHeight: '1.8', color: '#555' }}>
                      {[
                        infoCliente.logradouro,
                        infoCliente.numero ? `Nº ${infoCliente.numero}` : '',
                        infoCliente.complemento,
                        infoCliente.bairro,
                        infoCliente.cidade,
                        infoCliente.estado,
                        infoCliente.cep ? `CEP: ${infoCliente.cep}` : ''
                      ].filter(Boolean).join(', ')}
                    </p>
                  ) : (
                    <p style={{ margin: '5px 0', color: '#999', fontStyle: 'italic' }}>Endereço não informado</p>
                  )}
                  {infoCliente.link_localizacao && (
                    <div style={{ marginTop: '10px' }}>
                      <a 
                        href={infoCliente.link_localizacao} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#007bff', textDecoration: 'underline' }}
                      >
                        🔗 Abrir localização no mapa
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Kit Solar */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #ffc107', paddingBottom: '8px' }}>
                  ☀️ Kit Solar
                </h4>
                {infoCliente.kit_solar_cliente && (infoCliente.kit_solar_cliente.marca_inversor || infoCliente.kit_solar_cliente.marca_modulo) ? (
                  <div style={{ backgroundColor: '#fff3cd', padding: '15px', borderRadius: '8px' }}>
                    {/* Inversor */}
                    <div style={{ marginBottom: '15px' }}>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#856404' }}>⚡ Inversor:</strong>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', paddingLeft: '10px' }}>
                        {infoCliente.kit_solar_cliente.marca_inversor && (
                          <div>
                            <strong>Marca:</strong>
                            <p style={{ margin: '3px 0', color: '#555' }}>{infoCliente.kit_solar_cliente.marca_inversor}</p>
                          </div>
                        )}
                        {infoCliente.kit_solar_cliente.potencia_inversor && (
                          <div>
                            <strong>Potência:</strong>
                            <p style={{ margin: '3px 0', color: '#555' }}>{infoCliente.kit_solar_cliente.potencia_inversor} kW</p>
                          </div>
                        )}
                        {infoCliente.kit_solar_cliente.quantidade_inversor && (
                          <div>
                            <strong>Quantidade:</strong>
                            <p style={{ margin: '3px 0', color: '#555' }}>{infoCliente.kit_solar_cliente.quantidade_inversor}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Módulo */}
                    <div>
                      <strong style={{ display: 'block', marginBottom: '8px', color: '#856404' }}>🔆 Módulo:</strong>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', paddingLeft: '10px' }}>
                        {infoCliente.kit_solar_cliente.marca_modulo && (
                          <div>
                            <strong>Marca:</strong>
                            <p style={{ margin: '3px 0', color: '#555' }}>{infoCliente.kit_solar_cliente.marca_modulo}</p>
                          </div>
                        )}
                        {infoCliente.kit_solar_cliente.potencia_modulo && (
                          <div>
                            <strong>Potência:</strong>
                            <p style={{ margin: '3px 0', color: '#555' }}>{infoCliente.kit_solar_cliente.potencia_modulo} W</p>
                          </div>
                        )}
                        {infoCliente.kit_solar_cliente.quantidade_modulo && (
                          <div>
                            <strong>Quantidade:</strong>
                            <p style={{ margin: '3px 0', color: '#555' }}>{infoCliente.kit_solar_cliente.quantidade_modulo}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                    <p style={{ margin: '5px 0', color: '#999', fontStyle: 'italic' }}>Kit solar não cadastrado para este cliente</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '15px 30px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={closeInfoClienteModal}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Baixa de Vistoria */}
      {showBaixaModal && vistoriaParaBaixa && createPortal(
        <div className="modal-overlay" onClick={closeBaixaModal}>
          <div className="modal-content modal-baixa-vistoria" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📝 Baixa de Vistoria - {vistoriaParaBaixa.cliente?.nome_completo || 'N/A'}</h3>
              {vistoriaMedicaoFechada(vistoriaParaBaixa) && (
                <span className="badge bg-secondary" title="Medição fechada em Serviços">🔒 Medição fechada — somente consulta</span>
              )}
              {autoSaveStatus === 'saved' && (
                <div
                  className="autosave-badge autosave-badge-saved"
                  aria-live="polite"
                >
                  <span className="autosave-check" aria-hidden="true">✓</span>
                  Salvo{autoSaveLastAt ? ` às ${autoSaveLastAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
                </div>
              )}
              <button className="modal-close" onClick={closeBaixaModal}>✕</button>
            </div>

            <div className="modal-body modal-baixa-body">
              {(() => {
                // Permitir edição mesmo após baixa
                const isReadOnly = vistoriaMedicaoFechada(vistoriaParaBaixa)
                
                return (
              <form onSubmit={(e) => { e.preventDefault(); handleSaveBaixa(); }}>
                {/* Seção: Identificação */}
                <div className="form-section-baixa">
                  <h4 className="section-title-baixa">🔑 Identificação</h4>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa">
                      <label htmlFor="tipo_padrao_id">Tipo de Padrão *</label>
                    <select
                        id="tipo_padrao_id"
                        value={formBaixa.tipo_padrao_id}
                        onChange={(e) => setFormBaixa({ ...formBaixa, tipo_padrao_id: e.target.value })}
                      required
                        className="form-control-baixa"
                        disabled={isReadOnly}
                    >
                      <option value="">Selecione...</option>
                      {tiposPadrao.map(tipo => (
                          <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                      ))}
                    </select>
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="ligacao_padrao">Ligação do Padrão *</label>
                    <select
                        id="ligacao_padrao"
                        value={formBaixa.ligacao_padrao}
                        onChange={(e) => setFormBaixa({ ...formBaixa, ligacao_padrao: e.target.value })}
                      required
                        className="form-control-baixa"
                        disabled={isReadOnly}
                    >
                      <option value="">Selecione...</option>
                      <option value="Monofasico">Monofásico</option>
                      <option value="Bifasico">Bifásico</option>
                      <option value="Trifasico">Trifásico</option>
                    </select>
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="data_vistoria">Data da Vistoria</label>
                      <input
                        type="date"
                        id="data_vistoria"
                        value={formBaixa.data_vistoria}
                        onChange={(e) => setFormBaixa({ ...formBaixa, data_vistoria: e.target.value })}
                        className="form-control-baixa"
                        disabled={isReadOnly}
                      />
                    </div>
                </div>
              </div>

                {/* Seção: Locais de Instalação */}
                <div className="form-section-baixa">
                  <h4 className="section-title-baixa">🏠 Locais de Instalação</h4>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa full-width">
                      <label htmlFor="local_inversor">Local do Inversor</label>
                      <textarea
                        id="local_inversor"
                        value={formBaixa.local_inversor}
                        onChange={(e) => setFormBaixa({ ...formBaixa, local_inversor: e.target.value })}
                        className="form-control-baixa"
                        rows="2"
                        placeholder="Descreva onde será instalado o inversor"
                        disabled={isReadOnly}
                  />
                </div>
                  </div>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa">
                      <label htmlFor="posicao_poste_concessionaria">Posição do Poste</label>
                    <select
                        id="posicao_poste_concessionaria"
                        value={formBaixa.posicao_poste_concessionaria}
                        onChange={(e) => setFormBaixa({ ...formBaixa, posicao_poste_concessionaria: e.target.value })}
                        className="form-control-baixa"
                        disabled={isReadOnly}
                    >
                      <option value="">Selecione...</option>
                      <option value="Mesmo lado da rua">Mesmo lado da rua</option>
                      <option value="Outro lado da rua">Outro lado da rua</option>
                    </select>
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="conexao_inversor">Conexão do Inversor</label>
                    <select
                        id="conexao_inversor"
                        value={formBaixa.conexao_inversor}
                        onChange={(e) => setFormBaixa({ ...formBaixa, conexao_inversor: e.target.value })}
                        className="form-control-baixa"
                        disabled={isReadOnly}
                    >
                      <option value="">Selecione...</option>
                      <option value="Padrão de entrada">Padrão de entrada</option>
                      <option value="Quadro do cliente">Quadro do cliente</option>
                    </select>
                  </div>
                </div>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa full-width">
                      <label htmlFor="local_aterramento_sistema">Local de Aterramento</label>
                      <textarea
                        id="local_aterramento_sistema"
                        value={formBaixa.local_aterramento_sistema}
                        onChange={(e) => setFormBaixa({ ...formBaixa, local_aterramento_sistema: e.target.value })}
                        className="form-control-baixa"
                        rows="2"
                        placeholder="Descreva onde será instalado o aterramento"
                        disabled={isReadOnly}
                  />
                </div>
              </div>
                  </div>

                {/* Seção: Metragens */}
                <div className="form-section-baixa">
                  <h4 className="section-title-baixa">📏 Metragens (em metros)</h4>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa">
                      <label htmlFor="metragem_ca_inversor">C.A. Inversor</label>
                    <input
                      type="number"
                        id="metragem_ca_inversor"
                        value={formBaixa.metragem_ca_inversor}
                        onChange={(e) => setFormBaixa({ ...formBaixa, metragem_ca_inversor: e.target.value })}
                        className="form-control-baixa"
                      step="0.01"
                        min="0"
                        placeholder="0.00"
                        disabled={isReadOnly}
                    />
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="metragem_cc">C.C. (Cabos Solares)</label>
                    <input
                      type="number"
                        id="metragem_cc"
                        value={formBaixa.metragem_cc}
                        onChange={(e) => setFormBaixa({ ...formBaixa, metragem_cc: e.target.value })}
                        className="form-control-baixa"
                      step="0.01"
                        min="0"
                        placeholder="0.00"
                        disabled={isReadOnly}
                    />
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="metragem_ca_padrao_fases">C.A. Padrão - Fases</label>
                    <input
                      type="number"
                        id="metragem_ca_padrao_fases"
                        value={formBaixa.metragem_ca_padrao_fases}
                        onChange={(e) => setFormBaixa({ ...formBaixa, metragem_ca_padrao_fases: e.target.value })}
                        className="form-control-baixa"
                      step="0.01"
                        min="0"
                        placeholder="0.00"
                        disabled={isReadOnly}
                    />
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="percurso_aterramento">Percurso Aterramento</label>
                    <input
                      type="number"
                        id="percurso_aterramento"
                        value={formBaixa.percurso_aterramento}
                        onChange={(e) => setFormBaixa({ ...formBaixa, percurso_aterramento: e.target.value })}
                        className="form-control-baixa"
                      step="0.01"
                        min="0"
                        placeholder="0.00"
                        disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

                {/* Seção: Quantidades de Materiais */}
                <div className="form-section-baixa">
                  <h4 className="section-title-baixa">📦 Quantidades de Materiais</h4>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa">
                      <label htmlFor="qtd_conduletes_inversor">Conduletes Inversor</label>
                    <input
                      type="number"
                        id="qtd_conduletes_inversor"
                        value={formBaixa.qtd_conduletes_inversor}
                        onChange={(e) => setFormBaixa({ ...formBaixa, qtd_conduletes_inversor: e.target.value })}
                        className="form-control-baixa"
                        min="0"
                      placeholder="0"
                        disabled={isReadOnly}
                    />
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="qtd_eletroduto_inversor">Eletroduto Inversor</label>
                    <input
                      type="number"
                        id="qtd_eletroduto_inversor"
                        value={formBaixa.qtd_eletroduto_inversor}
                        onChange={(e) => setFormBaixa({ ...formBaixa, qtd_eletroduto_inversor: e.target.value })}
                        className="form-control-baixa"
                        min="0"
                      placeholder="0"
                        disabled={isReadOnly}
                    />
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="qtd_eletroduto_padrao">Eletroduto Padrão</label>
                    <input
                      type="number"
                        id="qtd_eletroduto_padrao"
                        value={formBaixa.qtd_eletroduto_padrao}
                        onChange={(e) => setFormBaixa({ ...formBaixa, qtd_eletroduto_padrao: e.target.value })}
                        className="form-control-baixa"
                        min="0"
                      placeholder="0"
                        disabled={isReadOnly}
                    />
                  </div>
                    <div className="form-group-baixa">
                      <label htmlFor="qtd_modulos_telhado">Quantos módulos comportam no telhado</label>
                    <input
                      type="number"
                        id="qtd_modulos_telhado"
                        value={formBaixa.qtd_modulos_telhado}
                        onChange={(e) => setFormBaixa({ ...formBaixa, qtd_modulos_telhado: e.target.value })}
                        className="form-control-baixa"
                        min="0"
                      placeholder="0"
                        disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

                {/* Seção: Caminhos de Imagens */}
                <div className="form-section-baixa">
                  <h4 className="section-title-baixa">📷 Upload de Imagens</h4>
                  <div className="form-row-baixa">
                    {/* Imagem do Padrão */}
                    <div className="form-group-baixa image-upload-group">
                      <label htmlFor="img_padrao">Imagem do Padrão</label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleImageSourceSelect('padrao')}
                            className="btn-select-image"
                            disabled={uploadingImages.padrao}
                          >
                            {uploadingImages.padrao ? '⏳ Enviando...' : '📷 Selecionar Imagem'}
                          </button>
                        )}
                        <input
                          type="file"
                          id="img_padrao"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) handleImageUpload('padrao', file)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.padrao}
                        />
                        {imageFiles.padrao && (
                          <div className="image-preview-container">
                            <img 
                              src={imageFiles.padrao.url} 
                              alt="Preview" 
                              className="image-preview" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => window.open(imageFiles.padrao.url, '_blank')}
                            />
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="btn-remove-image"
                                onClick={() => handleRemoveImage('padrao')}
                              >
                                ✕ Remover
                              </button>
                            )}
                          </div>
                        )}
                        {formBaixa.caminho_img_padrao && !imageFiles.padrao && (
                          <div className="image-url-display">
                            <a 
                              href={formBaixa.caminho_img_padrao} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                            >
                              📷 Ver imagem
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Imagem do Ramal de Entrada */}
                    <div className="form-group-baixa image-upload-group">
                      <label htmlFor="img_ramal">Foto do Ramal de Entrada</label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleImageSourceSelect('ramal')}
                            className="btn-select-image"
                            disabled={uploadingImages.ramal}
                          >
                            {uploadingImages.ramal ? '⏳ Enviando...' : '📷 Selecionar Imagem'}
                          </button>
                        )}
                        <input
                          type="file"
                          id="img_ramal"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) handleImageUpload('ramal', file)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.ramal}
                        />
                        {imageFiles.ramal && (
                          <div className="image-preview-container">
                            <img 
                              src={imageFiles.ramal.url} 
                              alt="Preview" 
                              className="image-preview" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => window.open(imageFiles.ramal.url, '_blank')}
                            />
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="btn-remove-image"
                                onClick={() => handleRemoveImage('ramal')}
                              >
                                ✕ Remover
                              </button>
                            )}
                          </div>
                        )}
                        {formBaixa.caminho_img_ramal && !imageFiles.ramal && (
                          <div className="image-url-display">
                            <a 
                              href={formBaixa.caminho_img_ramal} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                            >
                              📷 Ver imagem
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Imagem do Inversor */}
                    <div className="form-group-baixa image-upload-group">
                      <label htmlFor="img_inversor">Foto de onde será instalado o inversor</label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleImageSourceSelect('inversor')}
                            className="btn-select-image"
                            disabled={uploadingImages.inversor}
                          >
                            {uploadingImages.inversor ? '⏳ Enviando...' : '📷 Selecionar Imagem'}
                          </button>
                        )}
                    <input
                          type="file"
                          id="img_inversor"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) handleImageUpload('inversor', file)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.inversor}
                        />
                        {imageFiles.inversor && (
                          <div className="image-preview-container">
                            <img 
                              src={imageFiles.inversor.url} 
                              alt="Preview" 
                              className="image-preview" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => window.open(imageFiles.inversor.url, '_blank')}
                            />
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="btn-remove-image"
                                onClick={() => handleRemoveImage('inversor')}
                              >
                                ✕ Remover
                              </button>
                            )}
                          </div>
                        )}
                        {formBaixa.caminho_img_inversor && !imageFiles.inversor && (
                          <div className="image-url-display">
                            <a 
                              href={formBaixa.caminho_img_inversor} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                            >
                              📷 Ver imagem
                            </a>
                          </div>
                        )}
                      </div>
                  </div>

                    {/* Imagem da Fachada */}
                    <div className="form-group-baixa image-upload-group">
                      <label htmlFor="img_fachada">📷 Foto da Fachada do Local <span style={{color: '#dc3545'}}>*</span></label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleImageSourceSelect('fachada')}
                            className="btn-select-image"
                            disabled={uploadingImages.fachada}
                          >
                            {uploadingImages.fachada ? '⏳ Enviando...' : '📷 Selecionar Imagem'}
                          </button>
                        )}
                    <input
                          type="file"
                          id="img_fachada"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) handleImageUpload('fachada', file)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.fachada}
                        />
                        {imageFiles.fachada && (
                          <div className="image-preview-container">
                            <img 
                              src={imageFiles.fachada.url} 
                              alt="Preview" 
                              className="image-preview" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => window.open(imageFiles.fachada.url, '_blank')}
                            />
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="btn-remove-image"
                                onClick={() => handleRemoveImage('fachada')}
                              >
                                ✕ Remover
                              </button>
                            )}
                          </div>
                        )}
                        {formBaixa.caminho_img_fachada && !imageFiles.fachada && (
                          <div className="image-url-display">
                            <a 
                              href={formBaixa.caminho_img_fachada} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                            >
                              📷 Ver imagem
                            </a>
                          </div>
                        )}
                      </div>
                  </div>

                    {/* Imagem do Mapa */}
                    <div className="form-group-baixa image-upload-group">
                      <label htmlFor="img_mapa">🔗 Link do Mapa do Local da Instalação <span style={{color: '#dc3545'}}>*</span></label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleImageSourceSelect('mapa')}
                            className="btn-select-image"
                            disabled={uploadingImages.mapa}
                          >
                            {uploadingImages.mapa ? '⏳ Enviando...' : '📷 Selecionar Imagem'}
                          </button>
                        )}
                    <input
                          type="file"
                          id="img_mapa"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) handleImageUpload('mapa', file)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.mapa}
                        />
                        {imageFiles.mapa && (
                          <div className="image-preview-container">
                            <img 
                              src={imageFiles.mapa.url} 
                              alt="Preview" 
                              className="image-preview" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => window.open(imageFiles.mapa.url, '_blank')}
                            />
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="btn-remove-image"
                                onClick={() => handleRemoveImage('mapa')}
                              >
                                ✕ Remover
                              </button>
                            )}
                          </div>
                        )}
                        {formBaixa.caminho_img_mapa && !imageFiles.mapa && (
                          <div className="image-url-display">
                            <a 
                              href={formBaixa.caminho_img_mapa} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                            >
                              📷 Ver imagem
                            </a>
                          </div>
                        )}
                      </div>
                  </div>

                    {/* Imagem do Telhado - Múltiplas */}
                    <div className="form-group-baixa image-upload-group full-width">
                      <label htmlFor="img_multiple_telhado">Imagem do Telhado (Múltiplas)</label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleMultipleImageSourceSelect('telhado')}
                            className="btn-select-image"
                            disabled={uploadingImages.telhado}
                          >
                            {uploadingImages.telhado ? '⏳ Enviando...' : '📷 Selecionar Múltiplas Imagens'}
                          </button>
                        )}
                    <input
                          type="file"
                          id="img_multiple_telhado"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || [])
                            if (files.length > 0) handleMultipleImageUpload('telhado', files)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.telhado}
                        />
                        {multipleImageFiles.telhado && multipleImageFiles.telhado.length > 0 && (
                          <div className="multiple-images-grid">
                            {multipleImageFiles.telhado.map((img, index) => (
                              <div key={index} className="multiple-image-item">
                                <img 
                                  src={img.url} 
                                  alt={`Telhado ${index + 1}`} 
                                  className="image-preview" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => window.open(img.url, '_blank')}
                                />
                                {!isReadOnly && (
                                  <button
                                    type="button"
                                    className="btn-remove-image"
                                    onClick={() => handleRemoveMultipleImage('telhado', index)}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {formBaixa.caminho_img_telhado && (!multipleImageFiles.telhado || multipleImageFiles.telhado.length === 0) && (
                          <div className="image-url-display">
                            <span style={{ color: '#666', fontStyle: 'italic' }}>
                              Imagens salvas anteriormente - 
                              {(() => {
                                try {
                                  const urls = typeof formBaixa.caminho_img_telhado === 'string' 
                                    ? JSON.parse(formBaixa.caminho_img_telhado) 
                                    : formBaixa.caminho_img_telhado
                                  if (Array.isArray(urls)) {
                                    return urls.map((url, idx) => (
                                      <a 
                                        key={idx}
                                        href={url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff', marginLeft: '5px' }}
                                      >
                                        📷 Ver {idx + 1}
                                      </a>
                                    ))
                                  }
                                } catch (e) {
                                  return (
                                    <a 
                                      href={formBaixa.caminho_img_telhado} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff', marginLeft: '5px' }}
                                    >
                                      📷 Ver imagem
                                    </a>
                                  )
                                }
                                return null
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                  </div>

                    {/* Imagem da Estrutura - Múltiplas */}
                    <div className="form-group-baixa image-upload-group full-width">
                      <label htmlFor="img_multiple_estrutura">Imagem da Estrutura (Múltiplas)</label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleMultipleImageSourceSelect('estrutura')}
                            className="btn-select-image"
                            disabled={uploadingImages.estrutura}
                          >
                            {uploadingImages.estrutura ? '⏳ Enviando...' : '📷 Selecionar Múltiplas Imagens'}
                          </button>
                        )}
                    <input
                          type="file"
                          id="img_multiple_estrutura"
                          accept="image/*"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || [])
                            if (files.length > 0) handleMultipleImageUpload('estrutura', files)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.estrutura}
                        />
                        {multipleImageFiles.estrutura && multipleImageFiles.estrutura.length > 0 && (
                          <div className="multiple-images-grid">
                            {multipleImageFiles.estrutura.map((img, index) => (
                              <div key={index} className="multiple-image-item">
                                <img 
                                  src={img.url} 
                                  alt={`Estrutura ${index + 1}`} 
                                  className="image-preview" 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => window.open(img.url, '_blank')}
                                />
                                {!isReadOnly && (
                                  <button
                                    type="button"
                                    className="btn-remove-image"
                                    onClick={() => handleRemoveMultipleImage('estrutura', index)}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {formBaixa.caminho_img_estrutura && (!multipleImageFiles.estrutura || multipleImageFiles.estrutura.length === 0) && (
                          <div className="image-url-display">
                            <span style={{ color: '#666', fontStyle: 'italic' }}>
                              Imagens salvas anteriormente - 
                              {(() => {
                                try {
                                  const urls = typeof formBaixa.caminho_img_estrutura === 'string' 
                                    ? JSON.parse(formBaixa.caminho_img_estrutura) 
                                    : formBaixa.caminho_img_estrutura
                                  if (Array.isArray(urls)) {
                                    return urls.map((url, idx) => (
                                      <a 
                                        key={idx}
                                        href={url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff', marginLeft: '5px' }}
                                      >
                                        📷 Ver {idx + 1}
                                      </a>
                                    ))
                                  }
                                } catch (e) {
                                  return (
                                    <a 
                                      href={formBaixa.caminho_img_estrutura} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff', marginLeft: '5px' }}
                                    >
                                      📷 Ver imagem
                                    </a>
                                  )
                                }
                                return null
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                  </div>

                    {/* Imagem do Quadro */}
                    <div className="form-group-baixa image-upload-group">
                      <label htmlFor="img_quadro">Foto do Quadro de Distribuição</label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleImageSourceSelect('quadro')}
                            className="btn-select-image"
                            disabled={uploadingImages.quadro}
                          >
                            {uploadingImages.quadro ? '⏳ Enviando...' : '📷 Selecionar Imagem'}
                          </button>
                        )}
                    <input
                          type="file"
                          id="img_quadro"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) handleImageUpload('quadro', file)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.quadro}
                        />
                        {imageFiles.quadro && (
                          <div className="image-preview-container">
                            <img 
                              src={imageFiles.quadro.url} 
                              alt="Preview" 
                              className="image-preview" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => window.open(imageFiles.quadro.url, '_blank')}
                            />
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="btn-remove-image"
                                onClick={() => handleRemoveImage('quadro')}
                              >
                                ✕ Remover
                              </button>
                            )}
                  </div>
                        )}
                        {formBaixa.caminho_img_quadro && !imageFiles.quadro && (
                          <div className="image-url-display">
                            <a 
                              href={formBaixa.caminho_img_quadro} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                            >
                              📷 Ver imagem
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Imagem do Croqui */}
                    <div className="form-group-baixa image-upload-group">
                      <label htmlFor="img_croqui">📐 Croqui</label>
                      <div className="image-upload-container">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleImageSourceSelect('croqui')}
                            className="btn-select-image"
                            disabled={uploadingImages.croqui}
                          >
                            {uploadingImages.croqui ? '⏳ Enviando...' : '📷 Selecionar Imagem'}
                          </button>
                        )}
                        <input
                          type="file"
                          id="img_croqui"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0]
                            if (file) handleImageUpload('croqui', file)
                            e.target.value = ''
                          }}
                          className="image-upload-input-hidden"
                          style={{ display: 'none' }}
                          disabled={uploadingImages.croqui}
                        />
                        {imageFiles.croqui && (
                          <div className="image-preview-container">
                            <img 
                              src={imageFiles.croqui.url} 
                              alt="Preview" 
                              className="image-preview" 
                              style={{ cursor: 'pointer' }}
                              onClick={() => window.open(imageFiles.croqui.url, '_blank')}
                            />
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="btn-remove-image"
                                onClick={() => handleRemoveImage('croqui')}
                              >
                                ✕ Remover
                              </button>
                            )}
                          </div>
                        )}
                        {formBaixa.caminho_img_croqui && !imageFiles.croqui && (
                          <div className="image-url-display">
                            <a 
                              href={formBaixa.caminho_img_croqui} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                            >
                              📷 Ver imagem
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                </div>
              </div>

                {/* Seção: Parecer Final */}
                <div className="form-section-baixa">
                  <h4 className="section-title-baixa">✅ Parecer Final</h4>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa">
                      <label htmlFor="status_vistoria">Parecer final *</label>
                    <select
                        id="status_vistoria"
                        value={formBaixa.status}
                        onChange={(e) => setFormBaixa({ ...formBaixa, status: e.target.value })}
                        required
                        className="form-control-baixa"
                        disabled={isReadOnly}
                      >
                        <option value="">Selecione...</option>
                        <option value="Novo">Novo</option>
                        <option value="Aprovado">Aprovado</option>
                        <option value="Aprovado com Obra">Aprovado com Obra</option>
                        <option value="Cancelado">Cancelado</option>
                        <option value="Reprovado">Reprovado</option>
                    </select>
                  </div>
                  </div>
                </div>

                {/* Seção: Observações */}
                <div className="form-section-baixa">
                  <h4 className="section-title-baixa">📝 Observações</h4>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa full-width">
                      <label htmlFor="observacoes">Observações Gerais</label>
                      <textarea
                        id="observacoes"
                        value={formBaixa.observacoes}
                        onChange={(e) => setFormBaixa({ ...formBaixa, observacoes: e.target.value })}
                        className="form-control-baixa"
                        rows="4"
                        placeholder="Adicione observações relevantes sobre a vistoria"
                        disabled={isReadOnly}
                      />
                    </div>
                </div>
              </div>

                {/* Seção: Deslocamento */}
                <div className="form-section-baixa">
                  <h4 className="section-title-baixa">🚗 Deslocamento</h4>
                  <div className="form-row-baixa">
                    <div className="form-group-baixa">
                      <label htmlFor="km_rodado">Km Rodado</label>
                      <input
                        type="text"
                        id="km_rodado"
                        inputMode="decimal"
                        value={formBaixa.km_rodado}
                        onChange={(e) => {
                          const valor = e.target.value
                          const kmNum = parseFloat(String(valor).replace(',', '.')) || 0
                          setFormBaixa({
                            ...formBaixa,
                            km_rodado: valor,
                            // Abaixo de 100km é considerado moto; acima, mantém a seleção do usuário.
                            tipo_veiculo: kmNum >= 100 ? formBaixa.tipo_veiculo : (kmNum > 0 ? 'moto' : '')
                          })
                        }}
                        className="form-control-baixa"
                        placeholder="0"
                        disabled={isReadOnly}
                      />
                    </div>
                    {(parseFloat(String(formBaixa.km_rodado).replace(',', '.')) || 0) >= 100 && (
                      <div className="form-group-baixa">
                        <label>Tipo de veículo *</label>
                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', paddingTop: '8px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal' }}>
                            <input
                              type="checkbox"
                              checked={formBaixa.tipo_veiculo === 'carro'}
                              onChange={() => setFormBaixa({ ...formBaixa, tipo_veiculo: formBaixa.tipo_veiculo === 'carro' ? '' : 'carro' })}
                              disabled={isReadOnly}
                            />
                            🚗 Carro
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontWeight: 'normal' }}>
                            <input
                              type="checkbox"
                              checked={formBaixa.tipo_veiculo === 'moto'}
                              onChange={() => setFormBaixa({ ...formBaixa, tipo_veiculo: formBaixa.tipo_veiculo === 'moto' ? '' : 'moto' })}
                              disabled={isReadOnly}
                            />
                            🏍️ Moto
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  {(() => {
                    const deslocamento = calcularDeslocamentoBaixa()
                    if (!deslocamento) return null
                    const fmt = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                    return (
                      <div
                        className="form-row-baixa"
                        style={{
                          marginTop: '10px',
                          padding: '12px 16px',
                          background: '#f0f7ff',
                          border: '1px solid #cfe3ff',
                          borderRadius: '8px'
                        }}
                      >
                        <div style={{ width: '100%' }}>
                          <div style={{ fontSize: '13px', color: '#555' }}>
                            {fmt(deslocamento.valorKm)}/km × {String(formBaixa.km_rodado).replace('.', ',')} km = {fmt(deslocamento.base)}
                            {deslocamento.aplicaAlmoco && (
                              <> + almoço {fmt(deslocamento.valorAlmoco)}</>
                            )}
                          </div>
                          <div style={{ fontSize: '18px', fontWeight: 700, color: '#1565c0', marginTop: '4px' }}>
                            Valor do deslocamento: {fmt(deslocamento.total)}
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </div>

                <div className="modal-footer-baixa">
                  <div
                    className={`autosave-status autosave-status-${autoSaveStatus}`}
                    aria-live="polite"
                  >
                    {autoSaveStatus === 'saved' && `Salvo automaticamente${autoSaveLastAt ? ` às ${autoSaveLastAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}`}
                  </div>
                  <button
                    type="button"
                    className="btn-secondary-baixa"
                    onClick={closeBaixaModal}
                    disabled={isLoadingBaixa}
                  >
                    {isReadOnly ? 'Fechar' : 'Cancelar'}
                  </button>
                  {!isReadOnly && (
                <button
                  type="submit"
                      className="btn-primary-baixa"
                      disabled={isLoadingBaixa}
                >
                      {isLoadingBaixa ? 'Salvando...' : 'Salvar Baixa'}
                </button>
                  )}
                </div>
              </form>
                )
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Seleção de Fonte de Imagem (Mobile) */}
      {showImageSourceModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowImageSourceModal(false)}>
          <div className="modal-content modal-image-source" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📷 Selecionar Fonte da Imagem</h3>
              <button className="modal-close" onClick={() => setShowImageSourceModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="image-source-options">
                <button
                  type="button"
                  className="btn-image-source"
                  onClick={() => {
                    // Verificar se é campo de múltiplas imagens
                    const isMultiple = currentImageType === 'telhado' || currentImageType === 'estrutura'
                    if (isMultiple) {
                      selectMultipleImageSource(true)
                    } else {
                      selectImageSource(true)
                    }
                  }}
                >
                  <span className="icon-large">📷</span>
                  <span className="text">Tirar Foto</span>
                  <span className="subtitle">Usar a câmera do dispositivo</span>
                </button>
                <button
                  type="button"
                  className="btn-image-source"
                  onClick={() => {
                    // Verificar se é campo de múltiplas imagens
                    const isMultiple = currentImageType === 'telhado' || currentImageType === 'estrutura'
                    if (isMultiple) {
                      selectMultipleImageSource(false)
                    } else {
                      selectImageSource(false)
                    }
                  }}
                >
                  <span className="icon-large">🖼️</span>
                  <span className="text">Escolher da Galeria</span>
                  <span className="subtitle">Selecionar foto existente</span>
                </button>
              </div>
            </div>
            <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                onClick={() => setShowImageSourceModal(false)}
                >
                Cancelar
                </button>
              </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Detalhes da Vistoria */}
      {showDetalhesModal && vistoriaDetalhes && createPortal(
        <div className="modal-overlay" onClick={() => setShowDetalhesModal(false)}>
          <div className="modal-content" style={{ maxWidth: '900px', width: '95%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📋 Detalhes da Vistoria #{vistoriaDetalhes.id}</h3>
              <button
                className="modal-close"
                onClick={() => setShowDetalhesModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body" style={{ padding: '30px' }}>
              {/* Informações Básicas */}
              <div className="form-section" style={{ marginBottom: '30px' }}>
                <h4 style={{ marginBottom: '20px', color: '#667eea', borderBottom: '2px solid #667eea', paddingBottom: '10px' }}>
                  📋 Informações Básicas
                </h4>
                <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                  <div className="detail-item">
                    <strong>ID da Ordem:</strong>
                    <p>{vistoriaDetalhes.id || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Cliente:</strong>
                    <p>{vistoriaDetalhes.cliente?.nome_completo || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>CPF:</strong>
                    <p>{vistoriaDetalhes.cliente?.cpf || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Telefone:</strong>
                    <p>{vistoriaDetalhes.cliente?.telefone || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong>
                    <p>{vistoriaDetalhes.cliente?.email || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Tipo de Serviço:</strong>
                    <p>{vistoriaDetalhes.tipo_servico?.nome || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong>
                    <span className={`badge ${statusBadgeClass(vistoriaDetalhes.status)}`}>
                      {vistoriaDetalhes.status || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Data de Execução:</strong>
                    <p>{vistoriaDetalhes.data_execucao ? new Date(vistoriaDetalhes.data_execucao).toLocaleDateString('pt-BR') : 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Data de Criação:</strong>
                    <p>{vistoriaDetalhes.created_at ? new Date(vistoriaDetalhes.created_at).toLocaleDateString('pt-BR') : 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Empresa:</strong>
                    <p>{vistoriaDetalhes.empresa?.empresa || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <strong>Usuário Responsável:</strong>
                    <p>
                      {vistoriaDetalhes.ordem_servico_responsaveis && vistoriaDetalhes.ordem_servico_responsaveis.length > 0
                        ? vistoriaDetalhes.ordem_servico_responsaveis.map((resp, index) => (
                            <span key={resp.id || index}>
                              {resp.usuario?.nome || 'N/A'}
                              {resp.usuario?.email && ` (${resp.usuario.email})`}
                              {index < vistoriaDetalhes.ordem_servico_responsaveis.length - 1 && ', '}
                            </span>
                          ))
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Observações */}
              {(vistoriaDetalhes.obs_operacional || vistoriaDetalhes.obs_campo) && (
                <div className="form-section" style={{ marginBottom: '30px' }}>
                  <h4 style={{ marginBottom: '20px', color: '#667eea', borderBottom: '2px solid #667eea', paddingBottom: '10px' }}>
                    ℹ️ Observações
                  </h4>
                  {vistoriaDetalhes.obs_operacional && (
                    <div style={{ marginBottom: '15px' }}>
                      <strong>Observações Operacionais:</strong>
                      <p style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', whiteSpace: 'pre-wrap', marginTop: '5px' }}>
                        {vistoriaDetalhes.obs_operacional}
                  </p>
                </div>
              )}
                  {vistoriaDetalhes.obs_campo && (
                    <div>
                      <strong>Observações de Campo:</strong>
                      <p style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', whiteSpace: 'pre-wrap', marginTop: '5px' }}>
                        {vistoriaDetalhes.obs_campo}
                      </p>
                    </div>
                  )}
                    </div>
                  )}

              {/* Informações de Baixa da Vistoria */}
              {vistoriaDetalhes.vistoria_completa && (
                <div className="form-section" style={{ marginBottom: '30px' }}>
                  <h4 style={{ marginBottom: '20px', color: '#28a745', borderBottom: '2px solid #28a745', paddingBottom: '10px' }}>
                    📋 Informações de Baixa da Vistoria
                  </h4>
                  
                  {(() => {
                    const baixa = vistoriaDetalhes.vistoria_completa
                    return (
                      <>
                        {/* Configurações de Ligação */}
                        {(baixa.tipo_padrao_id || baixa.ligacao_padrao) && (
                          <div style={{ marginBottom: '20px' }}>
                            <h5 style={{ color: '#28a745', marginBottom: '10px' }}>⚡ Configurações de Ligação</h5>
                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                              {baixa.tipo_padrao_id && (
                                <div className="detail-item">
                                  <strong>Tipo de Padrão:</strong>
                                  <p>{baixa.tipo_padrao?.nome || `ID: ${baixa.tipo_padrao_id}`}</p>
                                </div>
                              )}
                              {baixa.ligacao_padrao && (
                                <div className="detail-item">
                                  <strong>Ligação Padrão:</strong>
                                  <p>{baixa.ligacao_padrao}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Locais de Instalação */}
                        {(baixa.local_inversor || baixa.posicao_poste_concessionaria || baixa.conexao_inversor || baixa.local_aterramento_sistema) && (
                          <div style={{ marginBottom: '20px' }}>
                            <h5 style={{ color: '#28a745', marginBottom: '10px' }}>🏠 Locais de Instalação</h5>
                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                              {baixa.local_inversor && (
                                <div className="detail-item">
                                  <strong>Local do Inversor:</strong>
                                  <p>{baixa.local_inversor}</p>
                                </div>
                              )}
                              {baixa.posicao_poste_concessionaria && (
                                <div className="detail-item">
                                  <strong>Posição do Poste:</strong>
                                  <p>{baixa.posicao_poste_concessionaria}</p>
                                </div>
                              )}
                              {baixa.conexao_inversor && (
                                <div className="detail-item">
                                  <strong>Conexão do Inversor:</strong>
                                  <p>{baixa.conexao_inversor}</p>
                                </div>
                              )}
                              {baixa.local_aterramento_sistema && (
                                <div className="detail-item">
                                  <strong>Local de Aterramento:</strong>
                                  <p>{baixa.local_aterramento_sistema}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Metragens */}
                        {(baixa.metragem_ca_inversor || baixa.metragem_cc || baixa.metragem_ca_padrao_fases || baixa.percurso_aterramento) && (
                          <div style={{ marginBottom: '20px' }}>
                            <h5 style={{ color: '#28a745', marginBottom: '10px' }}>📏 Metragens (metros)</h5>
                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                              {baixa.metragem_ca_inversor && (
                                <div className="detail-item">
                                  <strong>C.A Inversor:</strong>
                                  <p>{baixa.metragem_ca_inversor}m</p>
                                </div>
                              )}
                              {baixa.metragem_cc && (
                                <div className="detail-item">
                                  <strong>C.C:</strong>
                                  <p>{baixa.metragem_cc}m</p>
                                </div>
                              )}
                              {baixa.metragem_ca_padrao_fases && (
                                <div className="detail-item">
                                  <strong>C.A Padrão Fases:</strong>
                                  <p>{baixa.metragem_ca_padrao_fases}m</p>
                                </div>
                              )}
                              {baixa.percurso_aterramento && (
                                <div className="detail-item">
                                  <strong>Percurso Aterramento:</strong>
                                  <p>{baixa.percurso_aterramento}m</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Quantidades de Materiais */}
                        {(baixa.qtd_conduletes_inversor || baixa.qtd_eletroduto_inversor || baixa.qtd_eletroduto_padrao) && (
                          <div style={{ marginBottom: '20px' }}>
                            <h5 style={{ color: '#28a745', marginBottom: '10px' }}>🔧 Quantidades de Materiais</h5>
                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                              {baixa.qtd_conduletes_inversor && (
                                <div className="detail-item">
                                  <strong>Conduletes Inversor:</strong>
                                  <p>{baixa.qtd_conduletes_inversor}</p>
                                </div>
                              )}
                              {baixa.qtd_eletroduto_inversor && (
                                <div className="detail-item">
                                  <strong>Eletroduto Inversor:</strong>
                                  <p>{baixa.qtd_eletroduto_inversor}</p>
                                </div>
                              )}
                              {baixa.qtd_eletroduto_padrao && (
                                <div className="detail-item">
                                  <strong>Eletroduto Padrão:</strong>
                                  <p>{baixa.qtd_eletroduto_padrao}</p>
                                </div>
                              )}
                              {baixa.qtd_modulos_telhado && (
                                <div className="detail-item">
                                  <strong>Quantos módulos comportam no telhado:</strong>
                                  <p>{baixa.qtd_modulos_telhado}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Imagens */}
                        {(baixa.caminho_img_padrao || baixa.caminho_img_ramal || baixa.caminho_img_inversor || baixa.caminho_img_fachada || baixa.caminho_img_mapa || baixa.caminho_img_telhado || baixa.caminho_img_quadro || baixa.caminho_img_estrutura || baixa.caminho_img_croqui) && (
                          <div style={{ marginBottom: '20px' }}>
                            <h5 style={{ color: '#28a745', marginBottom: '10px' }}>📸 Imagens</h5>
                            <div className="fotos-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                              {baixa.caminho_img_padrao && (
                                <div className="foto-item">
                                  <img 
                                    src={baixa.caminho_img_padrao} 
                                    alt="Padrão" 
                                    className="foto-thumbnail"
                                    onClick={() => window.open(baixa.caminho_img_padrao, '_blank')}
                                  />
                                  <div>Padrão</div>
                                </div>
                              )}
                              {baixa.caminho_img_ramal && (
                                <div className="foto-item">
                                  <img 
                                    src={baixa.caminho_img_ramal} 
                                    alt="Ramal de Entrada" 
                                    className="foto-thumbnail"
                                    onClick={() => window.open(baixa.caminho_img_ramal, '_blank')}
                                  />
                                  <div>Ramal de Entrada</div>
                                </div>
                              )}
                              {baixa.caminho_img_inversor && (
                                <div className="foto-item">
                                  <img 
                                    src={baixa.caminho_img_inversor} 
                                    alt="Inversor" 
                                    className="foto-thumbnail"
                                    onClick={() => window.open(baixa.caminho_img_inversor, '_blank')}
                                  />
                                  <div>Inversor</div>
                                </div>
                              )}
                              {baixa.caminho_img_fachada && (
                                <div className="foto-item">
                                  <img 
                                    src={baixa.caminho_img_fachada} 
                                    alt="Fachada" 
                                    className="foto-thumbnail"
                                    onClick={() => window.open(baixa.caminho_img_fachada, '_blank')}
                                  />
                                  <div>Fachada</div>
                                </div>
                              )}
                              {baixa.caminho_img_mapa && (
                                <div className="foto-item">
                                  <img 
                                    src={baixa.caminho_img_mapa} 
                                    alt="Mapa" 
                                    className="foto-thumbnail"
                                    onClick={() => window.open(baixa.caminho_img_mapa, '_blank')}
                                  />
                                  <div>Mapa</div>
                                </div>
                              )}
                              {baixa.caminho_img_telhado && (() => {
                                try {
                                  const telhadoUrls = typeof baixa.caminho_img_telhado === 'string' 
                                    ? JSON.parse(baixa.caminho_img_telhado) 
                                    : baixa.caminho_img_telhado
                                  const urls = Array.isArray(telhadoUrls) ? telhadoUrls : [baixa.caminho_img_telhado]
                                  return urls.map((url, idx) => (
                                    <div key={`telhado-${idx}`} className="foto-item">
                                      <img 
                                        src={url} 
                                        alt={`Telhado ${idx + 1}`} 
                                        className="foto-thumbnail"
                                        onClick={() => window.open(url, '_blank')}
                                      />
                                      <div>Telhado {urls.length > 1 ? `${idx + 1}/${urls.length}` : ''}</div>
                                    </div>
                                  ))
                                } catch (e) {
                                  return (
                                    <div className="foto-item">
                                      <img 
                                        src={baixa.caminho_img_telhado} 
                                        alt="Telhado" 
                                        className="foto-thumbnail"
                                        onClick={() => window.open(baixa.caminho_img_telhado, '_blank')}
                                      />
                                      <div>Telhado</div>
                                    </div>
                                  )
                                }
                              })()}
                              {baixa.caminho_img_quadro && (
                                <div className="foto-item">
                                  <img 
                                    src={baixa.caminho_img_quadro} 
                                    alt="Quadro" 
                                    className="foto-thumbnail"
                                    onClick={() => window.open(baixa.caminho_img_quadro, '_blank')}
                                  />
                                  <div>Quadro</div>
                                </div>
                              )}
                              {baixa.caminho_img_croqui && (
                                <div className="foto-item">
                                  <img 
                                    src={baixa.caminho_img_croqui} 
                                    alt="Croqui" 
                                    className="foto-thumbnail"
                                    onClick={() => window.open(baixa.caminho_img_croqui, '_blank')}
                                  />
                                  <div>📐 Croqui</div>
                                </div>
                              )}
                              {baixa.caminho_img_estrutura && (() => {
                                try {
                                  const estruturaUrls = typeof baixa.caminho_img_estrutura === 'string' 
                                    ? JSON.parse(baixa.caminho_img_estrutura) 
                                    : baixa.caminho_img_estrutura
                                  const urls = Array.isArray(estruturaUrls) ? estruturaUrls : [baixa.caminho_img_estrutura]
                                  return urls.map((url, idx) => (
                                    <div key={`estrutura-${idx}`} className="foto-item">
                                      <img 
                                        src={url} 
                                        alt={`Estrutura ${idx + 1}`} 
                                        className="foto-thumbnail"
                                        onClick={() => window.open(url, '_blank')}
                                      />
                                      <div>Estrutura {urls.length > 1 ? `${idx + 1}/${urls.length}` : ''}</div>
                                    </div>
                                  ))
                                } catch (e) {
                                  return (
                                    <div className="foto-item">
                                      <img 
                                        src={baixa.caminho_img_estrutura} 
                                        alt="Estrutura" 
                                        className="foto-thumbnail"
                                        onClick={() => window.open(baixa.caminho_img_estrutura, '_blank')}
                                      />
                                      <div>Estrutura</div>
                                    </div>
                                  )
                                }
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Data da Vistoria e Observações */}
                        {(baixa.data_vistoria || baixa.observacoes) && (
                          <div style={{ marginBottom: '20px' }}>
                            <h5 style={{ color: '#28a745', marginBottom: '10px' }}>📝 Informações Adicionais</h5>
                            <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
                              {baixa.data_vistoria && (
                                <div className="detail-item">
                                  <strong>Data da Vistoria:</strong>
                                  <p>{new Date(baixa.data_vistoria).toLocaleDateString('pt-BR')}</p>
                                </div>
                              )}
                              {baixa.observacoes && (
                                <div className="detail-item">
                                  <strong>Observações:</strong>
                                  <p style={{ whiteSpace: 'pre-wrap', marginTop: '5px' }}>{baixa.observacoes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ padding: '20px 30px', borderTop: '1px solid #e8f0fe', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDetalhesModal(false)}
              >
                ✕ Fechar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Loading das Vistorias */}
      {isLoadingVistorias && createPortal(
        <div 
          className="modal-overlay" 
          style={{ zIndex: 2000, backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="modal-content" 
            style={{ maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-body" style={{ padding: '50px 30px' }}>
              <div style={{ marginBottom: '25px' }}>
                <div className="pdf-loading-spinner"></div>
              </div>
              <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>Carregando Vistorias</h3>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Buscando os dados, isso pode levar alguns instantes...</p>
              <p style={{ margin: '10px 0 0 0', color: '#999', fontSize: '12px', fontStyle: 'italic' }}>Por favor, aguarde</p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Auto-save (salvando/erro) */}
      {showBaixaModal && (autoSaveStatus === 'saving' || autoSaveStatus === 'error') && createPortal(
        <div
          className="modal-overlay autosave-modal-overlay"
          style={{ zIndex: 3000, backgroundColor: 'rgba(0, 0, 0, 0.35)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="modal-content autosave-modal-content"
            style={{ maxWidth: '340px', width: '90%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.35)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-body" style={{ padding: '36px 28px' }}>
              <div style={{ marginBottom: '18px' }}>
                {autoSaveStatus === 'saving' ? (
                  <div className="pdf-loading-spinner"></div>
                ) : (
                  <div style={{ fontSize: '42px', lineHeight: 1 }}>⚠️</div>
                )}
              </div>
              {autoSaveStatus === 'saving' ? (
                <>
                  <h3 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '18px', fontWeight: 600 }}>Salvando...</h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>Gravando as informações na base de dados.</p>
                </>
              ) : (
                <>
                  <h3 style={{ margin: '0 0 8px 0', color: '#dc3545', fontSize: '18px', fontWeight: 600 }}>
                    {autoSaveErrorOffline ? 'Tentando salvar...' : 'Não foi possível salvar'}
                  </h3>
                  <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '13px' }}>
                    {autoSaveErrorOffline
                      ? 'Sem conexão no momento. Não foi possível salvar as informações.'
                      : (autoSaveErrorMessage || 'Não foi possível salvar as informações.')}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button
                      type="button"
                      className="btn-secondary-baixa"
                      onClick={() => setAutoSaveStatus('idle')}
                    >
                      Fechar
                    </button>
                    <button
                      type="button"
                      className="btn-primary-baixa"
                      onClick={() => persistBaixaDraft({ silent: true })}
                    >
                      Tentar novamente
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Loading do PDF */}
      {showPdfLoadingModal && createPortal(
        <div 
          className="modal-overlay" 
          style={{ zIndex: 2000, backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            className="modal-content" 
            style={{ maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-body" style={{ padding: '50px 30px' }}>
              <div style={{ marginBottom: '25px' }}>
                <div className="pdf-loading-spinner"></div>
              </div>
              <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>Gerando Relatório PDF</h3>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Isso pode levar alguns instantes...</p>
              <p style={{ margin: '10px 0 0 0', color: '#999', fontSize: '12px', fontStyle: 'italic' }}>Por favor, aguarde</p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && createPortal(
        <div 
          className="modal-overlay" 
          style={{ zIndex: 2000, backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={() => { setShowSuccessModal(false); setSuccessWarning('') }}
        >
          <div 
            className="modal-content" 
            style={{ maxWidth: '450px', width: '90%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-body" style={{ padding: '40px 30px' }}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  backgroundColor: '#d4edda',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontSize: '40px'
                }}>
                  ✅
                </div>
              </div>
              <h3 style={{ margin: '0 0 15px 0', color: '#28a745', fontSize: '22px', fontWeight: '600' }}>Sucesso!</h3>
              <p style={{ margin: '0 0 25px 0', color: '#333', fontSize: '16px', lineHeight: '1.5' }}>
                {successMessage}
              </p>
              {successWarning && (
                <div
                  role="alert"
                  style={{
                    margin: '0 0 20px 0',
                    padding: '12px 16px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    color: '#856404',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    textAlign: 'left'
                  }}
                >
                  <strong>⚠️ E-mail não enviado:</strong> {successWarning}
                </div>
              )}
              <button
                onClick={() => { setShowSuccessModal(false); setSuccessWarning('') }}
                style={{
                  padding: '12px 32px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                  color: 'white',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-1px)'
                  e.target.style.boxShadow = '0 6px 16px rgba(40, 167, 69, 0.4)'
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)'
                  e.target.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)'
                }}
              >
                👍 Entendi
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Vistorias
